import React, { useState, useEffect, useMemo } from "react";
import { auth, db, googleProvider } from "./firebase";
import { SOFI_REFERRAL_URL } from "./partners";
import { CREDIT_CARD_BENEFITS, GENERAL_CREDIT_CARD_NAMES } from "./creditCardBenefits";
import { getLogoUrl } from "./logos";
import { signInWithPopup, signOut, onAuthStateChanged, reauthenticateWithPopup, deleteUser } from "firebase/auth";
import {
  collection, addDoc, deleteDoc, doc, onSnapshot,
  query, orderBy, getDoc, setDoc, arrayUnion, arrayRemove, increment, getDocs
} from "firebase/firestore";
import "./App.css";

const CATALOG = [
  {
    id: "streaming", label: "Streaming Video", icon: "🎬", lastVerified: "Sep 2026",
    services: [
      { name: "Netflix", icon: "🎬", tiers: [
        { label: "Standard with Ads", amount: 8.99 },
        { label: "Standard", amount: 19.99 },
        { label: "Premium", amount: 26.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Disney+", icon: "🏰", tiers: [
        { label: "Basic (Ads)", amount: 11.99 },
        { label: "Premium", amount: 18.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Hulu", icon: "📺", tiers: [
        { label: "With Ads", amount: 11.99 },
        { label: "No Ads", amount: 18.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Max (HBO)", icon: "👑", tiers: [
        { label: "With Ads", amount: 10.99 },
        { label: "Ad-Free", amount: 18.49 },
        { label: "Ultimate", amount: 22.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Apple TV+", icon: "🍎", tiers: [
        { label: "Individual", amount: 12.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Amazon Prime Video", icon: "📦", tiers: [
        { label: "With Ads", amount: 8.99 },
        { label: "Ad-Free", amount: 11.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Peacock", icon: "🦚", tiers: [
        { label: "Select", amount: 8.99 },
        { label: "Premium", amount: 12.99 },
        { label: "Premium Plus", amount: 19.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Paramount+", icon: "⭐", tiers: [
        { label: "Essential", amount: 8.99 },
        { label: "With Showtime", amount: 13.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "ESPN+", icon: "🏆", tiers: [
        { label: "Select (formerly ESPN+)", amount: 13.99 },
        { label: "ESPN Unlimited", amount: 31.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "YouTube Premium", icon: "▶️", tiers: [
        { label: "Individual", amount: 13.99 },
        { label: "Family", amount: 22.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Fubo TV", icon: "📡", tiers: [
        { label: "Pro", amount: 79.99 },
        { label: "Elite", amount: 89.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Crunchyroll", icon: "🎌", tiers: [
        { label: "Fan", amount: 7.99 },
        { label: "Mega Fan", amount: 9.99 },
        { label: "Ultimate Fan", amount: 14.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "NBA League Pass", icon: "🏀", tiers: [
        { label: "Team Pass", amount: 9.99 },
        { label: "League Pass", amount: 14.99 },
        { label: "Premium", amount: 19.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "MLB.TV", icon: "⚾", tiers: [
        { label: "Single Team (Yearly)", amount: 12.49 },
        { label: "All Teams (Yearly)", amount: 24.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Mubi", icon: "🎞️", tiers: [
        { label: "Monthly", amount: 14.99 },
        { label: "Annual", amount: 11.99 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "music", label: "Music & Audio", icon: "🎵", lastVerified: "Sep 2026",
    services: [
      { name: "Spotify", icon: "🎵", tiers: [
        { label: "Individual", amount: 12.99 },
        { label: "Duo", amount: 16.99 },
        { label: "Family", amount: 19.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Apple Music", icon: "🎶", tiers: [
        { label: "Individual", amount: 10.99 },
        { label: "Family", amount: 16.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "YouTube Music", icon: "🎼", tiers: [
        { label: "Individual", amount: 10.99 },
        { label: "Family", amount: 16.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Tidal", icon: "🌊", tiers: [
        { label: "Individual", amount: 10.99 },
        { label: "HiFi Plus", amount: 19.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Audible", icon: "📚", tiers: [
        { label: "Plus", amount: 7.95 },
        { label: "Premium Plus", amount: 14.95 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "SiriusXM", icon: "📻", tiers: [
        { label: "Streaming", amount: 9.99 },
        { label: "All Access", amount: 22.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Pandora Plus", icon: "🎧", tiers: [
        { label: "Plus", amount: 4.99 },
        { label: "Premium", amount: 9.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Kindle Unlimited", icon: "📖", tiers: [
        { label: "Monthly", amount: 11.99 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "ai", label: "AI Tools", icon: "🤖", lastVerified: "Sep 2026",
    services: [
      { name: "ChatGPT Plus", icon: "🤖", tiers: [
        { label: "Plus", amount: 20.00 },
        { label: "Pro", amount: 200.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Claude Pro", icon: "💜", tiers: [
        { label: "Pro", amount: 20.00 },
        { label: "Team", amount: 30.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Microsoft Copilot Pro", icon: "🪟", tiers: [
        { label: "Individual", amount: 20.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Gemini Advanced", icon: "🔷", tiers: [
        { label: "Google AI Pro", amount: 19.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Midjourney", icon: "🎨", tiers: [
        { label: "Basic", amount: 10.00 },
        { label: "Standard", amount: 30.00 },
        { label: "Pro", amount: 60.00 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Perplexity Pro", icon: "🔍", tiers: [
        { label: "Pro", amount: 20.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Grammarly Premium", icon: "✍️", tiers: [
        { label: "Premium", amount: 12.00 },
        { label: "Business", amount: 15.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Notion AI", icon: "📝", tiers: [
        { label: "Add-on", amount: 10.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Jasper", icon: "🧠", tiers: [
        { label: "Creator", amount: 39.00 },
        { label: "Pro", amount: 59.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "ElevenLabs", icon: "🔊", tiers: [
        { label: "Starter", amount: 5.00 },
        { label: "Creator", amount: 22.00 },
        { label: "Pro", amount: 99.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "GitHub Copilot", icon: "🐙", tiers: [
        { label: "Individual", amount: 10.00 },
        { label: "Business", amount: 19.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Cursor", icon: "⌨️", tiers: [
        { label: "Pro", amount: 20.00 },
        { label: "Business", amount: 40.00 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "social", label: "Social & Creator", icon: "📱", lastVerified: "Sep 2026",
    services: [
      { name: "X Premium (Twitter)", icon: "𝕏", tiers: [
        { label: "Basic", amount: 3.00 },
        { label: "Premium", amount: 8.00 },
        { label: "Premium+", amount: 16.00 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Snapchat+", icon: "👻", tiers: [
        { label: "Monthly", amount: 3.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "LinkedIn Premium", icon: "💼", tiers: [
        { label: "Career", amount: 39.99 },
        { label: "Business", amount: 59.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Discord Nitro", icon: "🎮", tiers: [
        { label: "Basic", amount: 2.99 },
        { label: "Nitro", amount: 9.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Patreon", icon: "🎁", tiers: [
        { label: "Varies by creator", amount: 5.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Meta Verified", icon: "📘", tiers: [
        { label: "Monthly", amount: 14.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Substack", icon: "📰", tiers: [
        { label: "Varies by newsletter", amount: 5.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Beehiiv", icon: "🐝", tiers: [
        { label: "Scale", amount: 39.00 },
        { label: "Max", amount: 99.00 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "shopping", label: "Shopping & Delivery", icon: "🛒", lastVerified: "Sep 2026",
    services: [
      { name: "Amazon Prime", icon: "📦", tiers: [
        { label: "Monthly", amount: 14.99 },
        { label: "Annual", amount: 11.58 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Walmart+", icon: "🏪", tiers: [
        { label: "Monthly", amount: 12.95 },
        { label: "Annual", amount: 8.17 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "DoorDash DashPass", icon: "🚗", tiers: [
        { label: "Individual", amount: 9.99 },
        { label: "Family", amount: 19.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Uber One", icon: "🚕", tiers: [
        { label: "Monthly", amount: 9.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Instacart+", icon: "🛍️", tiers: [
        { label: "Monthly", amount: 9.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Shipt", icon: "🏎️", tiers: [
        { label: "Monthly", amount: 10.99 },
        { label: "Annual", amount: 7.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "HelloFresh", icon: "🥗", tiers: [
        { label: "2 people / 2 meals", amount: 59.94 },
        { label: "2 people / 4 meals", amount: 95.88 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Costco Membership", icon: "🏢", tiers: [
        { label: "Gold Star", amount: 5.42 },
        { label: "Executive", amount: 10.83 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Sam's Club", icon: "🏬", tiers: [
        { label: "Club", amount: 5.00 },
        { label: "Plus", amount: 10.00 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "fitness", label: "Health & Fitness", icon: "💪", lastVerified: "Sep 2026",
    services: [
      { name: "Peloton App", icon: "🚴", tiers: [
        { label: "App One", amount: 12.99 },
        { label: "App+", amount: 28.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Planet Fitness", icon: "💪", tiers: [
        { label: "Classic", amount: 15.00 },
        { label: "Black Card", amount: 24.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "LA Fitness", icon: "🏋️", tiers: [
        { label: "Single Club", amount: 34.99 },
        { label: "Multi-Club", amount: 44.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Equinox", icon: "⚡", tiers: [
        { label: "Single Club", amount: 185.00 },
        { label: "All Access", amount: 260.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "MyFitnessPal Premium", icon: "🥗", tiers: [
        { label: "Monthly", amount: 19.99 },
        { label: "Annual", amount: 9.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Calm", icon: "🧘", tiers: [
        { label: "Monthly", amount: 14.99 },
        { label: "Annual", amount: 6.25 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Headspace", icon: "🧠", tiers: [
        { label: "Monthly", amount: 12.99 },
        { label: "Annual", amount: 5.83 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Noom", icon: "📊", tiers: [
        { label: "Monthly", amount: 70.00 },
        { label: "Annual", amount: 20.83 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Strava", icon: "🏃", tiers: [
        { label: "Subscriber", amount: 11.99 },
        { label: "Annual", amount: 7.92 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "learning", label: "Learning & Language", icon: "📚", lastVerified: "Sep 2026",
    services: [
      { name: "Duolingo Plus", icon: "🦜", tiers: [
        { label: "Super (Monthly)", amount: 12.99 },
        { label: "Super (Annual)", amount: 7.00 },
        { label: "Max", amount: 29.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Pimsleur", icon: "🗣️", tiers: [
        { label: "One Language", amount: 19.99 },
        { label: "All Access", amount: 29.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Rosetta Stone", icon: "🌍", tiers: [
        { label: "One Language", amount: 11.99 },
        { label: "All Languages", amount: 14.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Babbel", icon: "💬", tiers: [
        { label: "Monthly", amount: 13.95 },
        { label: "Annual", amount: 6.95 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Coursera Plus", icon: "🎓", tiers: [
        { label: "Monthly", amount: 59.00 },
        { label: "Annual", amount: 34.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "MasterClass", icon: "🏅", tiers: [
        { label: "Individual", amount: 10.00 },
        { label: "Duo", amount: 15.00 },
        { label: "Family", amount: 20.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Skillshare", icon: "✏️", tiers: [
        { label: "Monthly", amount: 32.00 },
        { label: "Annual", amount: 13.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Brilliant", icon: "💡", tiers: [
        { label: "Monthly", amount: 24.99 },
        { label: "Annual", amount: 12.49 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "software", label: "Software & Cloud", icon: "💻", lastVerified: "Sep 2026",
    services: [
      { name: "Microsoft 365", icon: "🪟", tiers: [
        { label: "Personal", amount: 9.99 },
        { label: "Family", amount: 12.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Adobe Creative Cloud", icon: "🎨", tiers: [
        { label: "Photography (1TB)", amount: 19.99 },
        { label: "Single App", amount: 22.99 },
        { label: "CC Standard", amount: 54.99 },
        { label: "CC Pro", amount: 69.99 },
      ], defaultTier: 2, cycle: "monthly" },
      { name: "iCloud+", icon: "☁️", tiers: [
        { label: "50GB", amount: 0.99 },
        { label: "200GB", amount: 2.99 },
        { label: "2TB", amount: 9.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Google One", icon: "🔷", tiers: [
        { label: "100GB", amount: 1.99 },
        { label: "200GB", amount: 2.99 },
        { label: "2TB", amount: 9.99 },
      ], defaultTier: 1, cycle: "monthly" },
      { name: "Dropbox", icon: "📁", tiers: [
        { label: "Plus", amount: 11.99 },
        { label: "Professional", amount: 19.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "NordVPN", icon: "🔒", tiers: [
        { label: "Basic", amount: 12.99 },
        { label: "Plus", amount: 13.99 },
        { label: "Ultimate", amount: 15.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "1Password", icon: "🗝️", tiers: [
        { label: "Individual", amount: 2.99 },
        { label: "Family", amount: 4.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Canva Pro", icon: "🖼️", tiers: [
        { label: "Pro", amount: 15.00 },
        { label: "Teams", amount: 10.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Zapier", icon: "⚡", tiers: [
        { label: "Starter", amount: 19.99 },
        { label: "Professional", amount: 49.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Notion", icon: "📝", tiers: [
        { label: "Plus", amount: 10.00 },
        { label: "Business", amount: 18.00 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "JetBrains All Products", icon: "🧰", tiers: [
        { label: "Individual", amount: 28.90 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Setapp", icon: "📦", tiers: [
        { label: "Mac", amount: 9.99 },
        { label: "Mac + iOS", amount: 12.49 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Proton Mail", icon: "✉️", tiers: [
        { label: "Mail Plus", amount: 4.99 },
        { label: "Unlimited", amount: 12.99 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "gaming", label: "Gaming", icon: "🎮", lastVerified: "Sep 2026",
    services: [
      { name: "Xbox Game Pass", icon: "🎮", tiers: [
        { label: "Essential", amount: 9.99 },
        { label: "Premium", amount: 14.99 },
        { label: "PC Game Pass", amount: 13.99 },
        { label: "Ultimate", amount: 22.99 },
      ], defaultTier: 3, cycle: "monthly" },
      { name: "PlayStation Plus", icon: "🕹️", tiers: [
        { label: "Essential", amount: 10.99 },
        { label: "Extra", amount: 16.99 },
        { label: "Premium", amount: 19.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Nintendo Switch Online", icon: "🎮", tiers: [
        { label: "Individual", amount: 3.99 },
        { label: "Family", amount: 7.99 },
        { label: "Expansion Pack", amount: 4.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "EA Play", icon: "⚽", tiers: [
        { label: "EA Play", amount: 4.99 },
        { label: "EA Play Pro", amount: 14.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Apple Arcade", icon: "🍎", tiers: [
        { label: "Individual", amount: 6.99 },
        { label: "Family", amount: 6.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Twitch Turbo", icon: "💜", tiers: [
        { label: "Turbo", amount: 8.99 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "GeForce Now", icon: "☁️", tiers: [
        { label: "Performance", amount: 9.99 },
        { label: "Ultimate", amount: 19.99 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
  {
    id: "finance", label: "Credit Cards (Annual Fees)", icon: "💳", lastVerified: "Sep 2026",
    services: [
      { name: "Amex Platinum", icon: "💳", tiers: [
        { label: "$895/yr", amount: 74.58 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Chase Sapphire Reserve", icon: "💎", tiers: [
        { label: "$795/yr", amount: 66.25 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Amex Gold", icon: "🥇", tiers: [
        { label: "$325/yr", amount: 27.08 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Chase Sapphire Preferred", icon: "💙", tiers: [
        { label: "$95/yr", amount: 7.92 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Capital One Venture X", icon: "✈️", tiers: [
        { label: "$395/yr", amount: 32.92 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Citi Strata Premier", icon: "🏙️", tiers: [
        { label: "$95/yr", amount: 7.92 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Amex Blue Cash Preferred", icon: "💳", tiers: [
        { label: "$95/yr", amount: 7.92 },
      ], defaultTier: 0, cycle: "monthly" },
      { name: "Hilton Honors Amex Surpass", icon: "🏨", tiers: [
        { label: "$150/yr", amount: 12.50 },
      ], defaultTier: 0, cycle: "monthly" },
    ],
  },
];

const CATEGORY_COLORS = {
  streaming: "#E6F1FB", music: "#FAEEDA", ai: "#EEEDFE",
  social: "#FAECE7", shopping: "#EAF3DE", fitness: "#E1F5EE",
  learning: "#FEF3CD", software: "#F1EFE8", finance: "#FCE8E8", gaming: "#E8F4FD",
};

function toMonthly(amount, cycle) {
  if (cycle === "yearly") return amount / 12;
  if (cycle === "weekly") return amount * 4.33;
  return parseFloat(amount);
}

const SANITY_LIMIT = 1000;

function ServiceLogo({ name, fallback, size = 28, style = {} }) {
  const [failed, setFailed] = useState(false);
  const url = getLogoUrl(name);
  if (!url || failed) {
    return <span style={{ fontSize: size * 0.75, lineHeight: 1, ...style }}>{fallback}</span>;
  }
  return (
    <img
      src={url}
      alt={name}
      width={size}
      height={size}
      style={{ borderRadius: 6, objectFit: "contain", imageRendering: "crisp-edges", ...style }}
      onError={() => setFailed(true)}
    />
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00"); d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
}

// Dollars actually saved so far since a sub's renewal date passed.
// Counts elapsed billing cycles (inclusive of the renewal day itself),
// so a $10/mo sub whose renewal was today shows $10, after one month $20, etc.
function realizedAmount(s) {
  const days = daysUntil(s.date);
  if (days === null || days > 0) return 0;
  const cycleDays = s.cycle === "yearly" ? 365.25 : s.cycle === "weekly" ? 7 : 30.44;
  const cycles = Math.floor(-days / cycleDays) + 1;
  return cycles * parseFloat(s.amount);
}

// Roll a (possibly past) renewal date forward by whole cycles until it's in the future.
function advanceDateToFuture(dateStr, cycle) {
  if (!dateStr) return dateStr;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00"); d.setHours(0, 0, 0, 0);
  while (d <= today) {
    if (cycle === "yearly") d.setFullYear(d.getFullYear() + 1);
    else if (cycle === "weekly") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
  }
  return d.toISOString().slice(0, 10);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function firstOfMonth() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function getCategoryIcon(cat) {
  const found = CATALOG.find(c => c.id === cat);
  return found ? found.icon : "📦";
}

// ─── ONBOARDING WIZARD ───────────────────────────────────────────────────────
function OnboardingWizard({ user, onComplete, existingSubs }) {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState({});
  const [tierIdx, setTierIdx] = useState({});
  const [customPrices, setCustomPrices] = useState({}); // key → custom override amount
  const [showCustomInput, setShowCustomInput] = useState({}); // key → bool
  const [customServices, setCustomServices] = useState({}); // categoryId → [{ name, icon, amount }]
  const [addCustomOpen, setAddCustomOpen] = useState({}); // categoryId → bool
  const [customDraft, setCustomDraft] = useState({ name: "", amount: "", icon: "" });
  const [saving, setSaving] = useState(false);

  const current = CATALOG[step];
  const existingNames = new Set(existingSubs.map(s => s.name));

  function getAmount(service, tIdx, key) {
    if (customPrices[key] !== undefined) return parseFloat(customPrices[key]) || 0;
    const idx = tIdx ?? service.defaultTier ?? 0;
    return service.tiers[idx]?.amount ?? service.tiers[0].amount;
  }

  function toggleService(service) {
    const key = `${current.id}:${service.name}`;
    setSelected(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { ...service, category: current.id };
      return next;
    });
    if (tierIdx[key] === undefined) {
      setTierIdx(prev => ({ ...prev, [key]: service.defaultTier ?? 0 }));
    }
  }

  function selectTier(service, idx, e) {
    e.stopPropagation();
    const key = `${current.id}:${service.name}`;
    setTierIdx(prev => ({ ...prev, [key]: idx }));
    // Clear custom price when switching tiers
    setCustomPrices(prev => { const n = { ...prev }; delete n[key]; return n; });
    setShowCustomInput(prev => ({ ...prev, [key]: false }));
  }

  function toggleCustomInput(key, defaultAmt, e) {
    e.stopPropagation();
    setShowCustomInput(prev => {
      const showing = !prev[key];
      if (showing && customPrices[key] === undefined) {
        setCustomPrices(p => ({ ...p, [key]: defaultAmt }));
      }
      return { ...prev, [key]: showing };
    });
  }

  function isSelected(service) { return !!selected[`${current.id}:${service.name}`]; }

  function addCustomService() {
    const name = customDraft.name.trim();
    const amount = parseFloat(customDraft.amount);
    if (!name || !amount || isNaN(amount)) return;
    if (amount > SANITY_LIMIT) {
      alert(`$${amount.toFixed(2)}/mo? That's more than we can track with a straight face. Cap is $${SANITY_LIMIT}/mo.`);
      return;
    }
    const icon = customDraft.icon.trim() || current.icon;
    setCustomServices(prev => ({
      ...prev,
      [current.id]: [...(prev[current.id] || []), { name, icon, amount }],
    }));
    setCustomDraft({ name: "", amount: "", icon: "" });
    setAddCustomOpen(prev => ({ ...prev, [current.id]: false }));
  }

  function removeCustomService(categoryId, idx) {
    setCustomServices(prev => ({
      ...prev,
      [categoryId]: (prev[categoryId] || []).filter((_, i) => i !== idx),
    }));
  }

  async function finish() {
    setSaving(true);
    try {
      const toAdd = Object.values(selected).filter(s => !existingNames.has(s.name));
      for (const s of toAdd) {
        const key = `${s.category}:${s.name}`;
        const tIdx = tierIdx[key] ?? s.defaultTier ?? 0;
        const tier = s.tiers[tIdx];
        const finalAmount = getAmount(s, tIdx, key);
        const isCustom = customPrices[key] !== undefined;
        await addDoc(collection(db, "users", user.uid, "subscriptions"), {
          name: s.name, icon: s.icon, category: s.category,
          amount: finalAmount,
          tierLabel: isCustom ? "Custom" : tier.label,
          cycle: s.cycle, date: firstOfMonth(),
          status: "active", createdAt: new Date(),
        });
      }
      for (const [categoryId, services] of Object.entries(customServices)) {
        for (const s of services) {
          if (existingNames.has(s.name)) continue;
          await addDoc(collection(db, "users", user.uid, "subscriptions"), {
            name: s.name, icon: s.icon, category: categoryId,
            amount: s.amount,
            tierLabel: "Custom",
            cycle: "monthly", date: firstOfMonth(),
            status: "active", createdAt: new Date(),
          });
        }
      }
      await setDoc(doc(db, "users", user.uid, "meta", "profile"), { onboarded: true }, { merge: true });
    } catch (err) {
      console.error("Wizard save error:", err);
    } finally {
      onComplete();
    }
  }

  const customCount = Object.values(customServices).reduce((n, arr) => n + arr.length, 0);
  const totalSelected = Object.keys(selected).length + customCount;
  const totalMonthly =
    Object.values(selected).reduce((sum, s) => {
      const key = `${s.category}:${s.name}`;
      return sum + toMonthly(getAmount(s, tierIdx[key], key), s.cycle);
    }, 0) +
    Object.values(customServices).reduce(
      (sum, arr) => sum + arr.reduce((s2, c) => s2 + c.amount, 0),
      0,
    );

  const progress = ((step + 1) / CATALOG.length) * 100;

  return (
    <div className="wizard-overlay">
      <div className="wizard">
        <div className="wizard-header">
          <div className="wizard-logo">Sub<span>track</span></div>
          <div className="wizard-step-counter">{step + 1} / {CATALOG.length}</div>
        </div>

        <div className="wizard-progress-bar">
          <div className="wizard-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <div className="wizard-step-header">
          <div className="wizard-step-icon">{current.icon}</div>
          <div>
            <div className="wizard-step-title">{current.label}</div>
            <div className="wizard-step-sub">Tap what you pay for, then pick your plan</div>
          </div>
        </div>

        <div className="wizard-grid">
          {current.services.map(service => {
            const key = `${current.id}:${service.name}`;
            const sel = isSelected(service);
            const alreadyHave = existingNames.has(service.name);
            const tIdx = tierIdx[key] ?? service.defaultTier ?? 0;
            const activeTier = service.tiers[tIdx];
            const hasMultipleTiers = service.tiers.length > 1;
            return (
              <div
                key={service.name}
                className={`wizard-card ${sel ? "selected" : ""} ${alreadyHave ? "already" : ""}`}
                onClick={() => !alreadyHave && toggleService(service)}
              >
                {sel && <div className="wizard-check">✓</div>}
                <div className="wizard-card-icon" style={{ background: CATEGORY_COLORS[current.id] || "#F1EFE8" }}>
                  <ServiceLogo name={service.name} fallback={service.icon} size={30} />
                </div>
                <div className="wizard-card-name">{service.name}</div>
                {alreadyHave ? (
                  <div className="wizard-card-price already-tag">✓ Added</div>
                ) : sel && showCustomInput[key] ? (
                  // Custom price input mode
                  <div className="wizard-custom-wrap" onClick={e => e.stopPropagation()}>
                    <div className="wizard-custom-row">
                      <span className="wizard-custom-dollar">$</span>
                      <input
                        className="wizard-custom-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={customPrices[key] ?? activeTier.amount}
                        onChange={e => setCustomPrices(prev => ({ ...prev, [key]: e.target.value }))}
                        autoFocus
                      />
                      <span className="wizard-custom-mo">/mo</span>
                    </div>
                    <button
                      className="wizard-custom-done"
                      onClick={e => {
                        const val = parseFloat(customPrices[key]);
                        const maxTier = Math.max(...service.tiers.map(t => t.amount));
                        const limit = Math.min(maxTier * 5, SANITY_LIMIT);
                        if (!isNaN(val) && val > limit) {
                          alert(`$${val.toFixed(2)}/mo for ${service.name}? That's more than 5× the most expensive plan. Either you have a very special arrangement, or something's off. We've capped it at $${limit.toFixed(2)}.`);
                          setCustomPrices(prev => ({ ...prev, [key]: limit }));
                          return;
                        }
                        toggleCustomInput(key, activeTier.amount, e);
                      }}
                    >
                      ✓ Done
                    </button>
                  </div>
                ) : sel && hasMultipleTiers ? (
                  // Tier pills mode
                  <div className="wizard-tier-pills" onClick={e => e.stopPropagation()}>
                    {service.tiers.map((t, i) => (
                      <button
                        key={i}
                        className={`wizard-tier-pill ${i === tIdx ? "active" : ""}`}
                        onClick={e => selectTier(service, i, e)}
                      >
                        {t.label}
                      </button>
                    ))}
                    <div className="wizard-tier-price">
                      {customPrices[key] !== undefined
                        ? <span style={{ color: "#c05a2e" }}>${parseFloat(customPrices[key]).toFixed(2)}/mo (custom)</span>
                        : `$${activeTier.amount}/mo`}
                    </div>
                    <button
                      className="wizard-custom-link"
                      onClick={e => toggleCustomInput(key, activeTier.amount, e)}
                    >
                      My price is different
                    </button>
                  </div>
                ) : sel ? (
                  // Single tier selected
                  <div onClick={e => e.stopPropagation()}>
                    <div className="wizard-card-price" style={{ color: "#2d6a4f", fontWeight: 500 }}>
                      {customPrices[key] !== undefined
                        ? <span style={{ color: "#c05a2e" }}>${parseFloat(customPrices[key]).toFixed(2)}/mo</span>
                        : `$${activeTier.amount}/mo`}
                    </div>
                    <button
                      className="wizard-custom-link"
                      onClick={e => toggleCustomInput(key, activeTier.amount, e)}
                    >
                      My price is different
                    </button>
                  </div>
                ) : (
                  // Unselected
                  <div className="wizard-card-price">
                    {hasMultipleTiers ? `from $${service.tiers[0].amount}/mo` : `$${activeTier.amount}/mo`}
                  </div>
                )}
              </div>
            );
          })}

          {(customServices[current.id] || []).map((cs, i) => (
            <div key={`custom-${i}`} className="wizard-card selected">
              <button
                className="wizard-custom-remove"
                onClick={() => removeCustomService(current.id, i)}
                title="Remove"
              >✕</button>
              <div className="wizard-card-icon" style={{ background: CATEGORY_COLORS[current.id] || "#F1EFE8" }}>
                {cs.icon}
              </div>
              <div className="wizard-card-name">{cs.name}</div>
              <div className="wizard-card-price" style={{ color: "#c05a2e", fontWeight: 500 }}>
                ${cs.amount.toFixed(2)}/mo
              </div>
            </div>
          ))}

          {addCustomOpen[current.id] ? (
            <div className="wizard-card wizard-card-custom-form" onClick={e => e.stopPropagation()}>
              <input
                className="wizard-custom-name-input"
                placeholder="Service name"
                value={customDraft.name}
                onChange={e => setCustomDraft({ ...customDraft, name: e.target.value })}
                autoFocus
                maxLength={40}
              />
              <div className="wizard-custom-row">
                <input
                  className="wizard-custom-icon-input"
                  placeholder="🎯"
                  value={customDraft.icon}
                  onChange={e => setCustomDraft({ ...customDraft, icon: e.target.value })}
                  maxLength={2}
                />
                <span className="wizard-custom-dollar">$</span>
                <input
                  className="wizard-custom-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={customDraft.amount}
                  onChange={e => setCustomDraft({ ...customDraft, amount: e.target.value })}
                />
                <span className="wizard-custom-mo">/mo</span>
              </div>
              <div className="wizard-custom-actions">
                <button
                  className="wizard-custom-cancel"
                  onClick={() => {
                    setAddCustomOpen(prev => ({ ...prev, [current.id]: false }));
                    setCustomDraft({ name: "", amount: "", icon: "" });
                  }}
                >Cancel</button>
                <button
                  className="wizard-custom-add-btn"
                  onClick={addCustomService}
                  disabled={!customDraft.name.trim() || !customDraft.amount}
                >Add</button>
              </div>
            </div>
          ) : (
            <button
              className="wizard-card wizard-card-add"
              onClick={() => setAddCustomOpen(prev => ({ ...prev, [current.id]: true }))}
            >
              <div className="wizard-card-add-plus">+</div>
              <div className="wizard-card-add-label">Add your own</div>
              <div className="wizard-card-add-sub">Don't see it? Add a custom service</div>
            </button>
          )}
        </div>

        <div className="wizard-footer">
          <div className="wizard-tally">
            {totalSelected > 0 ? (
              <span>{totalSelected} selected · <strong>${totalMonthly.toFixed(2)}/mo</strong> · <strong>${(totalMonthly * 12).toFixed(0)}/yr</strong></span>
            ) : (
              <span className="wizard-tally-hint">Tap services you subscribe to</span>
            )}
          </div>
          <div className="wizard-actions">
            {step > 0 && (
              <button className="btn-wizard-back" onClick={() => setStep(s => s - 1)}>← Back</button>
            )}
            {step < CATALOG.length - 1 && (
              <button className="btn-wizard-next" onClick={() => setStep(s => s + 1)}>
                {current.services.some(sv => isSelected(sv)) ? "Next →" : "Skip →"}
              </button>
            )}
            {(step === CATALOG.length - 1 || totalSelected > 0) && (
              <button className="btn-wizard-finish" onClick={finish} disabled={saving}>
                {saving ? "Saving..." : `Finish${totalSelected > 0 ? ` & add ${totalSelected}` : ""}`}
              </button>
            )}
          </div>
          <div className="wizard-verified">
            Prices verified {current.lastVerified} · Always double-check your plan
          </div>
          <button className="wizard-skip-all" onClick={async () => {
            try {
              await setDoc(doc(db, "users", user.uid, "meta", "profile"), { onboarded: true }, { merge: true });
            } catch (e) { console.error(e); } finally { onComplete(); }
          }}>Skip all setup</button>
        </div>
      </div>
    </div>
  );
}

// ─── WHAT NOW CARD ───────────────────────────────────────────────────────────
function WhatNowCard({ monthlySavings }) {
  const [mode, setMode] = useState("save");
  const annualRate = mode === "invest" ? 0.07 : 0.033;
  const horizons = [1, 5, 10, 20];

  function futureValue(P, r, years) {
    if (P <= 0) return 0;
    const m = r / 12;
    const n = years * 12;
    return P * (((1 + m) ** n - 1) / m);
  }

  const fmt = n => Math.round(n).toLocaleString();

  return (
    <div className="whatnow-card">
      <div className="whatnow-header">
        <div className="whatnow-eyebrow">What's next?</div>
        <div className="whatnow-title">You're freeing up <strong>${fmt(monthlySavings)}/mo</strong>.</div>
        <div className="whatnow-sub">Don't let it sit in a checking account earning 0.01%. Park it somewhere it grows.</div>
      </div>

      <div className="whatnow-toggle">
        <button className={`whatnow-toggle-btn ${mode === "save" ? "active" : ""}`} onClick={() => setMode("save")}>
          🏦 Save it
        </button>
        <button className={`whatnow-toggle-btn ${mode === "invest" ? "active" : ""}`} onClick={() => setMode("invest")}>
          📈 Invest it <span className="whatnow-toggle-badge">Soon</span>
        </button>
      </div>

      <div className="whatnow-projection">
        <div className="whatnow-projection-label">
          At {mode === "invest" ? "7% (S&P 500 long-term avg)" : "3.30% APY"}, that becomes
        </div>
        <div className="whatnow-horizons">
          {horizons.map(years => (
            <div key={years} className="whatnow-horizon">
              <div className="whatnow-horizon-value">${fmt(futureValue(monthlySavings, annualRate, years))}</div>
              <div className="whatnow-horizon-label">in {years} {years === 1 ? "yr" : "yrs"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="whatnow-partners-label">
        Where to put it
      </div>
      {mode === "save" ? (
        <div className="whatnow-partners">
          <a href={SOFI_REFERRAL_URL} target="_blank" rel="noopener noreferrer" className="whatnow-partner">
            <div className="whatnow-partner-icon">🏦</div>
            <div className="whatnow-partner-info">
              <div className="whatnow-partner-name">SoFi Checking & Savings</div>
              <div className="whatnow-partner-desc">3.30% APY · no fees, no minimums · $25 sign-up bonus</div>
            </div>
            <div className="whatnow-partner-arrow">→</div>
          </a>
        </div>
      ) : (
        <div className="whatnow-coming-soon">
          <div className="whatnow-coming-soon-icon">🚧</div>
          <div className="whatnow-coming-soon-text">
            <div className="whatnow-coming-soon-title">Investing recommendations coming soon</div>
            <div className="whatnow-coming-soon-sub">I'm still vetting brokerages I'd actually recommend. Until then, the projection above shows what's possible.</div>
          </div>
        </div>
      )}

      <AffiliateDisclosure />
    </div>
  );
}

function AffiliateDisclosure() {
  const [open, setOpen] = useState(false);
  return (
    <div className="affiliate-disclosure">
      <button className="affiliate-disclosure-toggle" onClick={() => setOpen(o => !o)}>
        ⓘ How I earn income {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="affiliate-disclosure-body">
          If you open an account through these links, I may receive a referral fee — at no cost to you. This is how I try to earn a little income from this project, without selling your data, pushing credit-card ads, or any of that. I only share platforms I'd actually recommend.
        </div>
      )}
    </div>
  );
}

// ─── SAVINGS PANEL ───────────────────────────────────────────────────────────
// ─── CARDS PANEL ─────────────────────────────────────────────────────────────
function CardsPanel({ subs }) {
  const [referralOpen, setReferralOpen] = useState(false);
  const activeSubs = subs.filter(s => s.status !== "cancel_pending");
  const subNames = activeSubs.map(s => s.name.toLowerCase());

  const userCards = CREDIT_CARD_BENEFITS.map(card => {
    const sub = activeSubs.find(s =>
      s.category === "finance" &&
      (s.name.toLowerCase().includes(card.name.toLowerCase()) ||
        card.name.toLowerCase().includes(s.name.toLowerCase()))
    );
    return sub ? { card, sub } : null;
  }).filter(Boolean);

  const ownedCardDefs = userCards.map(({ card }) => card);

  const notOwnedCards = CREDIT_CARD_BENEFITS
    .filter(card => !ownedCardDefs.includes(card))
    .sort((a, b) => {
      const aOverlap = a.benefits.some(bn => bn.matchKeywords.length && bn.matchKeywords.some(kw => subNames.some(sn => sn.includes(kw))));
      const bOverlap = b.benefits.some(bn => bn.matchKeywords.length && bn.matchKeywords.some(kw => subNames.some(sn => sn.includes(kw))));
      if (aOverlap !== bOverlap) return aOverlap ? -1 : 1;
      if (!!a.referralUrl !== !!b.referralUrl) return a.referralUrl ? -1 : 1;
      return 0;
    });

  function isBenefitActive(benefit) {
    if (!benefit.matchKeywords.length) return null;
    return benefit.matchKeywords.some(kw => subNames.some(sn => sn.includes(kw)));
  }

  function getMatchingSubName(benefit) {
    for (const kw of benefit.matchKeywords) {
      const match = activeSubs.find(s => s.name.toLowerCase().includes(kw));
      if (match) return match.name;
    }
    return null;
  }

  if (userCards.length === 0) {
    return (
      <div className="section">
        <div className="cards-empty">
          <div className="cards-empty-icon">💳</div>
          <div className="cards-empty-title">No credit cards tracked yet</div>
          <div className="cards-empty-sub">
            Add your annual-fee cards in the setup wizard to see which benefits you're leaving on the table.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="cards-philosophy">
        This isn't an endorsement of credit cards — benefits are designed to encourage spending. But if annual-fee cards are already part of your life, or you're committed to getting a new one, you may as well make sure you're getting full value from them.
      </div>

      <div className="section-header">
        <div className="section-title">Your cards</div>
      </div>

      <div className="cards-list">
        {userCards.map(({ card, sub }) => {
          const subMatchableBenefits = card.benefits.filter(b => b.matchKeywords.length > 0 && b.annualValue !== null);
          const activeValueTotal = subMatchableBenefits.reduce((sum, b) => isBenefitActive(b) ? sum + b.annualValue : sum, 0);
          const unusedValueTotal = subMatchableBenefits.reduce((sum, b) => !isBenefitActive(b) ? sum + b.annualValue : sum, 0);
          const effectiveCost = card.annualFee - activeValueTotal;
          const isMaximizing = activeValueTotal >= card.annualFee;
          const isPrimarilyGeneral = GENERAL_CREDIT_CARD_NAMES.includes(card.name);
          const showCancelWarning = !isPrimarilyGeneral && subMatchableBenefits.length > 0 && activeValueTotal === 0;
          const days = daysUntil(sub.date);
          const renewalLabel = sub.date
            ? (days === 0 ? "Renews today" : days === 1 ? "Renews tomorrow" : days !== null && days <= 30 ? `Renews in ${days} days` : `Renews ${formatDate(sub.date)}`)
            : null;
          const renewingSoon = days !== null && days <= 7 && days >= 0;

          return (
            <div key={card.name} className="card-block">
              <div className="card-block-header">
                <div>
                  <div className="card-block-name">{card.name}</div>
                  <div className="card-block-fee">
                    ${card.annualFee}/yr annual fee
                    {renewalLabel && (
                      <span className={`card-renewal-tag ${renewingSoon ? "soon" : ""}`}>{renewalLabel}</span>
                    )}
                  </div>
                </div>
                <div>
                  {isMaximizing ? (
                    <span className="card-badge card-badge-green">Maximizing it</span>
                  ) : activeValueTotal > 0 ? (
                    <span className="card-badge card-badge-neutral">${effectiveCost}/yr net</span>
                  ) : null}
                </div>
              </div>

              <div className="card-benefits-list">
                {card.benefits.map(benefit => {
                  const active = isBenefitActive(benefit);
                  const isGeneral = active === null;
                  const matchedSub = !isGeneral && active ? getMatchingSubName(benefit) : null;

                  return (
                    <div key={benefit.name} className={`card-benefit ${active ? "active" : isGeneral ? "general" : "unused"}`}>
                      <div className="card-benefit-dot">
                        {active ? "✓" : isGeneral ? "·" : "○"}
                      </div>
                      <div className="card-benefit-info">
                        <div className="card-benefit-name">
                          {benefit.name}
                          {matchedSub && <span className="card-benefit-match"> — via {matchedSub}</span>}
                        </div>
                        <div className="card-benefit-desc">{benefit.description}</div>
                      </div>
                      <div className="card-benefit-value">
                        {benefit.annualValue ? `$${benefit.annualValue}/yr` : "% back"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {activeValueTotal > 0 && !isMaximizing && (
                <div className="card-net-row">
                  <div className="card-net-label">Effective cost after subscription benefits</div>
                  <div className="card-net-value">${effectiveCost}/yr</div>
                </div>
              )}

              {unusedValueTotal > 0 && (
                <div className="card-unused-row">
                  <div className="card-unused-label">Subscription benefits not used</div>
                  <div className="card-unused-value">${unusedValueTotal}/yr left on the table</div>
                </div>
              )}

              {showCancelWarning && (
                <div className="card-cancel-warn">
                  <strong>Worth re-evaluating.</strong> You're paying ${card.annualFee}/yr but not using any subscription-linked benefits. If the travel and general credits aren't covering the fee either, this card may not be paying its way.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Referral section */}
      <div className="card-referral-section">
        <button className="card-referral-toggle" onClick={() => setReferralOpen(o => !o)}>
          <span className="card-referral-toggle-icon">{referralOpen ? "−" : "+"}</span>
          Cards you don't have
          <span className="card-referral-toggle-count">{notOwnedCards.length}</span>
        </button>

        {referralOpen && (
          <>
            <div className="card-referral-disclaimer">
              Heads up: these are personal referral links from cards I actually carry. If you apply and get approved, we both earn a reward — at no extra cost to you. I'm only linking cards I'd recommend to a friend.
            </div>

            <div className="cards-list" style={{ marginTop: "1rem" }}>
              {notOwnedCards.map(card => {
                const relevantBenefits = card.benefits.filter(b =>
                  b.matchKeywords.length > 0 && b.annualValue &&
                  b.matchKeywords.some(kw => subNames.some(sn => sn.includes(kw)))
                );
                return (
                  <div key={card.name} className="card-block card-block-unowned">
                    <div className="card-block-header">
                      <div>
                        <div className="card-block-name">{card.name}</div>
                        <div className="card-block-fee">${card.annualFee}/yr annual fee</div>
                      </div>
                      {relevantBenefits.length > 0 && (
                        <span className="card-badge card-badge-amber">
                          ${relevantBenefits.reduce((s, b) => s + b.annualValue, 0)}/yr overlap
                        </span>
                      )}
                    </div>
                    {relevantBenefits.length > 0 && (
                      <div className="card-benefits-list">
                        {relevantBenefits.map(b => (
                          <div key={b.name} className="card-benefit general">
                            <div className="card-benefit-dot">›</div>
                            <div className="card-benefit-info">
                              <div className="card-benefit-name">{b.name}</div>
                              <div className="card-benefit-desc">{b.description}</div>
                            </div>
                            <div className="card-benefit-value">${b.annualValue}/yr</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {relevantBenefits.length === 0 && (
                      <div className="card-no-overlap">
                        No direct overlap with your current subscriptions.
                      </div>
                    )}
                    {card.referralUrl && (
                      <a
                        className="card-referral-apply"
                        href={card.referralUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {card.welcomeBonus
                          ? <>Apply — earn {card.welcomeBonus} <span className="card-referral-arrow">→</span></>
                          : <>Apply with referral <span className="card-referral-arrow">→</span></>}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SavingsPanel({ subs, user, onEdit, onThankYou, meta, onDismissCircuitNudge }) {
  const cancelPending = subs.filter(s => s.status === "cancel_pending");

  const projectedAnnualSavings = cancelPending.reduce((sum, s) =>
    sum + toMonthly(s.amount, s.cycle) * 12, 0);

  const accruing = cancelPending.reduce((sum, s) => sum + realizedAmount(s), 0);
  const lifetimeFromReactivated = subs.reduce((sum, s) => sum + (s.lifetimeSaved || 0), 0);
  const realizedSavings = accruing + lifetimeFromReactivated;

  async function reactivate(sub) {
    const accrued = realizedAmount(sub);
    const lifetimeSaved = (sub.lifetimeSaved || 0) + accrued;
    const date = advanceDateToFuture(sub.date, sub.cycle);
    await setDoc(doc(db, "users", user.uid, "subscriptions", sub.id),
      { status: "active", date, lifetimeSaved }, { merge: true });
  }

  if (cancelPending.length === 0) {
    return (
      <div className="savings-empty">
        <div className="savings-empty-icon">💰</div>
        <div className="savings-empty-title">No subscriptions marked for cancellation</div>
        <div className="savings-empty-sub">
          Go to your subscriptions and tap ✂ on any service you want to cancel.
          We'll track your projected and realized savings here.
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="savings-hero">
        <div className="savings-hero-card green">
          <div className="savings-hero-label">Projected annual savings</div>
          <div className="savings-hero-value">${projectedAnnualSavings.toFixed(2)}</div>
          <div className="savings-hero-sub">when all cancelled subs lapse</div>
        </div>
        <div className="savings-hero-card">
          <div className="savings-hero-label">Realized so far</div>
          <div className="savings-hero-value">${realizedSavings.toFixed(2)}</div>
          <div className="savings-hero-sub">accrued since cancellation</div>
        </div>
      </div>

      {projectedAnnualSavings > 0 && (
        <WhatNowCard monthlySavings={projectedAnnualSavings / 12} />
      )}

      {meta && !meta.circuitFeedbackDismissed && cancelPending.length > 0 && (
        <div className="circuit-nudge">
          <button className="circuit-nudge-close" onClick={onDismissCircuitNudge} aria-label="Dismiss">×</button>
          <div className="circuit-nudge-icon">🙌</div>
          <div className="circuit-nudge-title">You're running the full loop</div>
          <div className="circuit-nudge-body">
            Tracking, marking, watching savings grow — that's the whole point. If Subtrack's helping you, I'd genuinely love a quick note (and if you feel like throwing a coffee my way, all the better).
          </div>
          <button className="circuit-nudge-cta" onClick={() => { onThankYou(realizedSavings || projectedAnnualSavings); onDismissCircuitNudge(); }}>
            Send me a note →
          </button>
        </div>
      )}

      <div className="section-header" style={{ marginTop: "1.25rem" }}>
        <div className="section-title">Marked for cancellation</div>
      </div>

      <div className="cancel-list">
        {cancelPending.map(s => {
          const days = daysUntil(s.date);
          const realized = days !== null && days <= 0;
          const monthly = toMonthly(s.amount, s.cycle);
          const bg = CATEGORY_COLORS[s.category] || "#F1EFE8";
          return (
            <div key={s.id} className="cancel-item">
              <div className="sub-icon" style={{ background: bg }}>
                {s.icon || getCategoryIcon(s.category)}
              </div>
              <div className="cancel-info">
                <div className="cancel-name">{s.name}</div>
                <div className="cancel-detail">
                  {realized ? (
                    <span className="tag-realized">✓ Savings realized</span>
                  ) : s.date ? (
                    <span className="tag-pending">
                      Savings start {formatDate(s.date)}
                      {days !== null && days > 0 ? ` (${days}d)` : ""}
                    </span>
                  ) : (
                    <span className="tag-pending">Next renewal</span>
                  )}
                </div>
              </div>
              <div className="cancel-right">
                <div className="cancel-savings">${(monthly * 12).toFixed(0)}/yr</div>
                <div className="cancel-actions">
                  <button className="btn-edit" onClick={() => onEdit(s)} title="Edit subscription">✎</button>
                  <button className="btn-reactivate" onClick={() => reactivate(s)}>Keep it</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="savings-tip">
        💡 <strong>Tip:</strong> Realized savings only count after your last paid renewal date passes — we don't count money you haven't actually saved yet.
      </div>

      {/* Option B — contextual nudge once real savings are realized */}
      {realizedSavings > 0 && (
        <div className="savings-donate-realized">
          <div className="savings-donate-realized-amount">${realizedSavings.toFixed(0)} actually back in your pocket.</div>
          <div className="savings-donate-realized-text">
            If it helped you get one step closer to financial independence, I'd love to hear from ya.
          </div>
          <button className="btn-thank-you" onClick={() => onThankYou(realizedSavings)}>
            Let me know what helped! →
          </button>
        </div>
      )}

      {/* Option A — always-visible quiet footer at bottom */}
      <div className="savings-donate-footer">
        Subtrack is free — always. If it helped you get one step closer to financial independence,{" "}
        <a href="https://donate.stripe.com/14A6oH8Xr5u333K0wi2wU00" target="_blank" rel="noopener noreferrer">tip jar →</a>
      </div>
    </div>
  );
}

// ─── COMMUNITY SAVINGS CHART ─────────────────────────────────────────────────
function fmtDollars(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function CommunitySavings() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, "stats", "community"))
      .then(snap => { if (snap.exists()) setStats(snap.data()); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const projected = stats?.projected ?? 0;
  const realized = stats?.realized ?? 0;

  return (
    <div className="community-savings-wrap">
      <div className="community-savings-header">Community impact</div>
      <div className="community-savings-pills">
        <div className="community-pill community-pill--projected">
          <div className="community-pill-value">
            {loading ? "—" : fmtDollars(projected)}
          </div>
          <div className="community-pill-label">projected annual savings</div>
          <div className="community-pill-sub">subscriptions marked to cancel</div>
        </div>
        <div className="community-pill community-pill--realized">
          <div className="community-pill-value">
            {loading ? "—" : fmtDollars(realized)}
          </div>
          <div className="community-pill-label">realized so far</div>
          <div className="community-pill-sub">renewal dates already passed</div>
        </div>
      </div>
    </div>
  );
}

// ─── FEATURE REQUESTS ────────────────────────────────────────────────────────
function FeatureRequestPanel({ user }) {
  const [requests, setRequests] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadState, setLoadState] = useState("loading"); // "loading" | "ready" | "error"

  useEffect(() => {
    const q = query(collection(db, "featureRequests"), orderBy("votes", "desc"));
    return onSnapshot(q,
      snap => {
        setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadState("ready");
      },
      () => setLoadState("error")
    );
  }, []);

  const [submitError, setSubmitError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await addDoc(collection(db, "featureRequests"), {
        title: title.trim(),
        description: description.trim(),
        votes: 1,
        voters: [user.uid],
        createdBy: user.uid,
        createdAt: new Date(),
      });
      setTitle("");
      setDescription("");
      setShowForm(false);
    } catch (err) {
      setSubmitError("Failed to submit — please try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVote(req) {
    const hasVoted = req.voters?.includes(user.uid);
    const ref = doc(db, "featureRequests", req.id);
    try {
      if (hasVoted) {
        await setDoc(ref, { votes: increment(-1), voters: arrayRemove(user.uid) }, { merge: true });
      } else {
        await setDoc(ref, { votes: increment(1), voters: arrayUnion(user.uid) }, { merge: true });
      }
    } catch {
      // Vote failed silently — optimistic UI is fine here, snapshot will correct
    }
  }

  return (
    <div className="section">
      <div className="section-header">
        <div className="section-title">Feature requests</div>
        <button className="btn-add" onClick={() => setShowForm(v => !v)}>
          {showForm ? "Cancel" : "+ Request"}
        </button>
      </div>

      {showForm && (
        <form className="add-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="field" style={{ flex: 1 }}>
              <label>Feature title</label>
              <input
                placeholder="e.g. Export to CSV"
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                maxLength={100}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field" style={{ flex: 1 }}>
              <label>Description <span style={{ color: "#bbb", fontWeight: 400 }}>(optional)</span></label>
              <input
                placeholder="More detail about what you need..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                maxLength={300}
              />
            </div>
          </div>
          {submitError && <div className="feature-submit-error">{submitError}</div>}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit request"}
            </button>
          </div>
        </form>
      )}

      {loadState === "loading" && !showForm && (
        <div className="empty"><div>Loading requests…</div></div>
      )}

      {loadState === "error" && (
        <div className="empty">
          <div className="empty-icon">📡</div>
          <div>Couldn't load feature requests — check your connection.</div>
        </div>
      )}

      {loadState === "ready" && requests.length === 0 && !showForm && (
        <div className="empty">
          <div className="empty-icon">💡</div>
          <div>No feature requests yet.</div>
          <button className="btn-quick-add mt" onClick={() => setShowForm(true)}>+ Be the first</button>
        </div>
      )}

      {requests.length > 0 && (
        <div className="feature-list">
          {requests.map((req, i) => {
            const hasVoted = req.voters?.includes(user.uid);
            return (
              <div key={req.id} className="feature-item">
                <div className="feature-rank">{i + 1}</div>
                <div className="feature-body">
                  <div className="feature-title-text">{req.title}</div>
                  {req.description && (
                    <div className="feature-desc-text">{req.description}</div>
                  )}
                </div>
                <button
                  className={`feature-vote-btn${hasVoted ? " voted" : ""}`}
                  onClick={() => handleVote(req)}
                  title={hasVoted ? "Remove vote" : "Vote for this"}
                >
                  <span className="feature-vote-arrow">▲</span>
                  <span className="feature-vote-count">{req.votes}</span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="savings-tip" style={{ marginTop: "1rem" }}>
        💡 <strong>Vote</strong> on ideas you'd love to see, or submit your own. Most-voted features get built first.
      </div>
    </div>
  );
}

// ─── SUBSCRIPTION CARD ───────────────────────────────────────────────────────
function SubCard({ sub, onMarkCancel, onDelete, onUpdateAmount, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(sub.amount));
  const [confirmDelete, setConfirmDelete] = useState(false);

  const days = daysUntil(sub.date);
  const isSoon = days !== null && days <= 7 && days >= 0;
  const bg = CATEGORY_COLORS[sub.category] || "#F1EFE8";
  const cycleLabel = sub.cycle === "monthly" ? "/mo" : sub.cycle === "yearly" ? "/yr" : "/wk";

  function startEdit() {
    setDraft(String(sub.amount));
    setEditing(true);
  }

  async function commit() {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed >= 0 && parsed !== parseFloat(sub.amount)) {
      await onUpdateAmount(sub.id, parsed);
    }
    setEditing(false);
  }

  function cancel() {
    setDraft(String(sub.amount));
    setEditing(false);
  }

  return (
    <div className="sub-card">
      <div className="sub-icon" style={{ background: bg }}>
        <ServiceLogo name={sub.name} fallback={sub.icon || getCategoryIcon(sub.category)} size={26} />
      </div>
      <div className="sub-info">
        <div className="sub-name">{sub.name}</div>
        <div className="sub-meta">
          {sub.category}
          {days !== null && (
            <span className={`badge ${isSoon ? "badge-soon" : "badge-ok"}`}>
              {isSoon ? `In ${days} day${days === 1 ? "" : "s"}` : formatDate(sub.date)}
            </span>
          )}
        </div>
      </div>
      <div className="sub-right">
        {editing ? (
          <div className="sub-amount-edit">
            <span className="sub-amount-dollar">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft}
              autoFocus
              onChange={e => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={e => {
                if (e.key === "Enter") commit();
                else if (e.key === "Escape") cancel();
              }}
            />
          </div>
        ) : (
          <button className="sub-amount-btn" onClick={startEdit} title="Edit amount">
            ${parseFloat(sub.amount).toFixed(2)}
          </button>
        )}
        <div className="sub-cycle">{cycleLabel}</div>
      </div>
      <button className="btn-edit" onClick={() => onEdit(sub)} title="Edit subscription">✎</button>
      <button className="btn-scissors" onClick={() => onMarkCancel(sub)} title="Mark for cancellation">✂</button>
      {confirmDelete ? (
        <div className="btn-delete-confirm">
          <button className="btn-delete-yes" onClick={() => onDelete(sub.id)}>Delete</button>
          <button className="btn-delete-no" onClick={() => setConfirmDelete(false)}>Keep</button>
        </div>
      ) : (
        <button className="btn-delete" onClick={() => setConfirmDelete(true)}>✕</button>
      )}
    </div>
  );
}

// ─── EDIT SUBSCRIPTION MODAL ─────────────────────────────────────────────────
function EditSubModal({ sub, onSave, onClose }) {
  const [name, setName] = useState(sub.name || "");
  const [icon, setIcon] = useState(sub.icon || "");
  const [amount, setAmount] = useState(String(sub.amount || ""));
  const [cycle, setCycle] = useState(sub.cycle || "monthly");
  const [date, setDate] = useState(sub.date || "");
  const [status, setStatus] = useState(sub.status === "cancel_pending" ? "cancel_pending" : "active");
  const [monthsAgo, setMonthsAgo] = useState(1);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed < 0) return;

    let effectiveDate = date;
    if (status === "already_cancelled") {
      const d = new Date();
      d.setMonth(d.getMonth() - monthsAgo);
      effectiveDate = d.toISOString().split("T")[0];
    }

    setSaving(true);
    await onSave(sub.id, {
      name: name.trim(),
      icon: icon.trim(),
      amount: parsed,
      cycle,
      date: effectiveDate,
      status: status === "already_cancelled" ? "cancel_pending" : status,
    });
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Edit subscription</div>
          <button className="modal-close" aria-label="Close" onClick={onClose}>✕</button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="modal-row">
            <label className="modal-label">Icon</label>
            <input className="modal-input modal-input-icon" value={icon} onChange={e => setIcon(e.target.value)} placeholder="😊" maxLength={2} />
          </div>

          <div className="modal-row">
            <label className="modal-label">Name</label>
            <input className="modal-input" value={name} onChange={e => setName(e.target.value)} required />
          </div>

          <div className="modal-row">
            <label className="modal-label">Amount</label>
            <div className="modal-amount-wrap">
              <span className="modal-dollar">$</span>
              <input className="modal-input modal-input-amount" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} required />
            </div>
          </div>

          <div className="modal-row">
            <label className="modal-label">Billing cycle</label>
            <div className="modal-seg">
              {["monthly", "yearly", "weekly"].map(c => (
                <button key={c} type="button" className={`modal-seg-btn ${cycle === c ? "active" : ""}`} onClick={() => setCycle(c)}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="modal-row">
            <label className="modal-label">Renewal date</label>
            <input className="modal-input" type="date" value={date} onChange={e => setDate(e.target.value)}
              disabled={status === "already_cancelled"} />
          </div>

          <div className="modal-row modal-row-status">
            <label className="modal-label">Status</label>
            <div className="modal-status-opts">
              {[
                { value: "active", label: "Active" },
                { value: "cancel_pending", label: "Cancel pending" },
                { value: "already_cancelled", label: "Already cancelled" },
              ].map(opt => (
                <label key={opt.value} className={`modal-status-opt ${status === opt.value ? "active" : ""}`}>
                  <input type="radio" name="status" value={opt.value} checked={status === opt.value}
                    onChange={() => setStatus(opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {status === "already_cancelled" && (
            <div className="modal-row modal-row-months">
              <label className="modal-label">Months ago (max 3)</label>
              <div className="modal-months-input-wrap">
                <input
                  className="modal-input modal-input-months"
                  type="number"
                  min="1"
                  max="3"
                  value={monthsAgo}
                  onChange={e => setMonthsAgo(Math.min(3, Math.max(1, parseInt(e.target.value) || 1)))}
                />
                <span className="modal-months-label">month{monthsAgo !== 1 ? "s" : ""} ago</span>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [subs, setSubs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [activeTab, setActiveTab] = useState("subscriptions");
  const [form, setForm] = useState({
    name: "", amount: "", cycle: "monthly",
    date: firstOfMonth(), category: "streaming", icon: "",
  });
  // Retention + monetization state
  const [showAbout, setShowAbout] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [thankYouContext, setThankYouContext] = useState(null); // { savings, name }
  const [editingSub, setEditingSub] = useState(null);
  const [milestone, setMilestone] = useState(null); // { type, amount, name }
  const [dismissedMilestones, setDismissedMilestones] = useState(() => {
    try { return JSON.parse(localStorage.getItem("st_dismissed") || "{}"); } catch { return {}; }
  });
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [writeError, setWriteError] = useState(null);
  const [addingSub, setAddingSub] = useState(false);
  const [cookieDismissed, setCookieDismissed] = useState(() => {
    try { return !!localStorage.getItem("st_consent"); } catch { return false; }
  });
  const prevCancelCount = React.useRef(0);
  const prevMonthly = React.useRef(0);

  useEffect(() => {
    return onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!user) { setSubs([]); setMeta(null); return; }
    let cancelled = false;
    const q = query(collection(db, "users", user.uid, "subscriptions"), orderBy("createdAt", "desc"));
    const unsubSubs = onSnapshot(q,
      snap => { if (!cancelled) setSubs(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
      err => { if (!cancelled) setWriteError("Lost connection — your data will refresh when you're back online."); }
    );
    getDoc(doc(db, "users", user.uid, "meta", "profile"))
      .then(d => { if (!cancelled) setMeta(d.exists() ? d.data() : {}); })
      .catch(() => { if (!cancelled) setMeta({}); });
    return () => { cancelled = true; unsubSubs(); };
  }, [user]);

  useEffect(() => {
    if (meta !== null && !meta.onboarded) setShowWizard(true);
    if (meta !== null && meta.onboarded && !meta.tutorialSeen) setShowTutorial(true);
  }, [meta]);

  // ── RETENTION TRIGGER 1: First cancellation milestone ──────────────────────
  useEffect(() => {
    if (!user || !subs.length) return;
    const cancelPendingNow = subs.filter(s => s.status === "cancel_pending");
    const wasZero = prevCancelCount.current === 0;
    const isNowPositive = cancelPendingNow.length > 0;
    if (wasZero && isNowPositive && !dismissedMilestones["first_cancel"]) {
      const totalSavings = cancelPendingNow.reduce((sum, s) => sum + toMonthly(s.amount, s.cycle) * 12, 0);
      setMilestone({ type: "first_cancel", savings: totalSavings, name: cancelPendingNow[0].name });
    }
    prevCancelCount.current = cancelPendingNow.length;
  }, [subs]);

  // ── RETENTION TRIGGER 2: Realized savings celebration ──────────────────────
  useEffect(() => {
    if (!user || !subs.length) return;
    const justRealized = subs.filter(s => {
      if (s.status !== "cancel_pending") return false;
      const days = daysUntil(s.date);
      return days !== null && days <= 0 && days > -3; // realized in last 3 days
    });
    if (justRealized.length > 0 && !dismissedMilestones[`realized_${justRealized[0].id}`]) {
      const s = justRealized[0];
      setMilestone({ type: "realized", savings: toMonthly(s.amount, s.cycle) * 12, name: s.name, id: s.id });
    }
  }, [subs]);

  // ── RETENTION TRIGGER 3: Spend creep alert ─────────────────────────────────
  useEffect(() => {
    if (!user || !subs.length) return;
    const activeSubs = subs.filter(s => s.status === "active" || !s.status);
    const currentMonthly = activeSubs.reduce((sum, s) => sum + toMonthly(s.amount, s.cycle), 0);
    const prev = prevMonthly.current;
    if (prev > 0 && currentMonthly > prev + 10 && !dismissedMilestones["creep"]) {
      setMilestone({ type: "creep", increase: currentMonthly - prev, total: currentMonthly });
    }
    prevMonthly.current = currentMonthly;
  }, [subs]);

  const dismissMilestone = (key) => {
    const updated = { ...dismissedMilestones, [key]: true };
    setDismissedMilestones(updated);
    try { localStorage.setItem("st_dismissed", JSON.stringify(updated)); } catch {}
    setMilestone(null);
  };

  const handleSignIn = async () => {
    try { await signInWithPopup(auth, googleProvider); } catch (e) { console.error(e); }
  };
  const handleSignOut = async () => { await signOut(auth); setSubs([]); setMeta(null); };

  const handleDeleteAccount = async () => {
    try {
      // Delete all subscription documents
      const subsSnap = await getDocs(collection(db, "users", user.uid, "subscriptions"));
      await Promise.all(subsSnap.docs.map(d => deleteDoc(d.ref)));
      // Delete meta document
      await deleteDoc(doc(db, "users", user.uid, "meta", "profile"));
      // Delete the Firebase Auth user (may require recent sign-in)
      await deleteUser(user);
      setSubs([]);
      setMeta(null);
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        // Re-authenticate then retry
        await reauthenticateWithPopup(user, googleProvider);
        const subsSnap = await getDocs(collection(db, "users", user.uid, "subscriptions"));
        await Promise.all(subsSnap.docs.map(d => deleteDoc(d.ref)));
        await deleteDoc(doc(db, "users", user.uid, "meta", "profile"));
        await deleteUser(user);
        setSubs([]);
        setMeta(null);
      } else {
        throw err;
      }
    }
  };

  const handleAdd = async e => {
    e.preventDefault();
    if (!form.name || !form.amount || isNaN(form.amount)) return;
    const amount = parseFloat(form.amount);
    if (amount > SANITY_LIMIT) {
      alert(`$${amount.toFixed(2)}/mo? That's… a lot. Are you sure you're not confusing this with rent? We cap manual entries at $${SANITY_LIMIT}.`);
      return;
    }
    if (addingSub) return;
    try {
      setAddingSub(true);
      setWriteError(null);
      await addDoc(collection(db, "users", user.uid, "subscriptions"), {
        ...form, amount, status: "active", createdAt: new Date(),
      });
      setForm({ name: "", amount: "", cycle: "monthly", date: firstOfMonth(), category: "streaming", icon: "" });
      setShowForm(false);
    } catch {
      setWriteError("Couldn't save — check your connection and try again.");
    } finally {
      setAddingSub(false);
    }
  };

  const handleDelete = async id => {
    try { await deleteDoc(doc(db, "users", user.uid, "subscriptions", id)); }
    catch { setWriteError("Couldn't delete — check your connection and try again."); }
  };

  const handleUpdateAmount = async (id, newAmount) => {
    const parsed = parseFloat(newAmount);
    if (isNaN(parsed) || parsed < 0) return;
    try {
      await setDoc(doc(db, "users", user.uid, "subscriptions", id), { amount: parsed }, { merge: true });
    } catch { setWriteError("Couldn't update amount — check your connection and try again."); }
  };

  const handleEditSub = async (id, fields) => {
    try {
      await setDoc(doc(db, "users", user.uid, "subscriptions", id), fields, { merge: true });
    } catch { setWriteError("Couldn't save changes — check your connection and try again."); }
  };

  const handleMarkCancel = async sub => {
    try {
      await setDoc(doc(db, "users", user.uid, "subscriptions", sub.id), { status: "cancel_pending" }, { merge: true });
    } catch { setWriteError("Couldn't update — check your connection and try again."); }
  };

  const activeSubs = useMemo(() => subs.filter(s => s.status === "active" || !s.status), [subs]);
  const cancelPending = useMemo(() => subs.filter(s => s.status === "cancel_pending"), [subs]);

  const totalMonthly = useMemo(() => activeSubs.reduce((sum, s) => sum + toMonthly(s.amount, s.cycle), 0), [activeSubs]);
  const atRiskMonthly = useMemo(() => cancelPending.reduce((sum, s) => sum + toMonthly(s.amount, s.cycle), 0), [cancelPending]);
  const renewingSoon = useMemo(() => activeSubs.filter(s => { const d = daysUntil(s.date); return d !== null && d <= 7 && d >= 0; }).length, [activeSubs]);

  const upcoming = useMemo(() => activeSubs
    .map(s => ({ ...s, days: daysUntil(s.date) }))
    .filter(s => s.days !== null && s.days <= 14 && s.days >= 0)
    .sort((a, b) => a.days - b.days), [activeSubs]);

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="app">
      {showWizard && user && (
        <OnboardingWizard user={user} existingSubs={subs} onComplete={() => { setShowWizard(false); setActiveTab("subscriptions"); setShowTutorial(true); }} />
      )}

      {showTutorial && user && (
        <TutorialModal onClose={async () => {
          setShowTutorial(false);
          try { await setDoc(doc(db, "users", user.uid, "meta", "profile"), { tutorialSeen: true }, { merge: true }); } catch (e) { console.error(e); }
        }} />
      )}

      {editingSub && (
        <EditSubModal
          sub={editingSub}
          onSave={handleEditSub}
          onClose={() => setEditingSub(null)}
        />
      )}

      {showThankYou && (
        <ThankYouModal
          context={thankYouContext}
          onClose={() => setShowThankYou(false)}
        />
      )}

      {milestone && user && (
        <MilestoneToast
          milestone={milestone}
          onDismiss={() => dismissMilestone(
            milestone.type === "realized" ? `realized_${milestone.id}` : milestone.type
          )}
          onSupport={() => {
            dismissMilestone(milestone.type);
            setThankYouContext({ savings: milestone.savings, name: milestone.name });
            setShowThankYou(true);
          }}
          onSavings={() => { dismissMilestone(milestone.type); setActiveTab("savings"); }}
        />
      )}

      <header className={`header ${!user ? "header-dark" : ""}`}>
        <div className={`logo ${!user ? "logo-light" : ""}`}>Sub<span>track</span></div>
        {user ? (
          <div className="user-area">
            <button className="btn-wizard-launch" onClick={() => setShowWizard(true)}>✦ Add subscriptions</button>
            <img src={user.photoURL} alt={user.displayName || "User"} className="avatar" />
            <span className="user-name">{user.displayName?.split(" ")[0]}</span>
            <button
              className={`btn-settings-gear ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab(t => t === "settings" ? "subscriptions" : "settings")}
              title="Settings"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          </div>
        ) : (
          <div className="header-guest">
            <button className="btn-ghost-light" onClick={() => setShowAbout(a => !a)}>About</button>
            <button className="btn-google" onClick={handleSignIn}><GoogleIcon /> Sign in with Google</button>
          </div>
        )}
      </header>

      {!user && showAbout && (
        <AboutPage onBack={() => setShowAbout(false)} onSignIn={handleSignIn} />
      )}

      {!user && !showAbout && (
        <div className="landing">

          {/* Hero — warm, soft, trustworthy */}
          <div className="landing-hero">
            <div className="landing-eyebrow">
              <span className="eyebrow-dot" /> Helping people save money, privately
            </div>
            <h1 className="landing-h1">Finally know what<br/>you're paying for.</h1>
            <p className="landing-sub">The average person forgets about $205/year in subscriptions. Subtrack finds them all in 2 minutes — no bank connection, no credit card.</p>
            <button className="btn-cta" onClick={handleSignIn}><GoogleIcon /> See your real number</button>
            <div className="landing-trust">No bank connection · 2 min setup · Your data stays yours</div>

            {/* Community savings — real aggregates from Firestore */}
            <CommunitySavings />
          </div>

          {/* Warm ticker */}
          <div className="ticker-wrap">
            <div className="ticker">
              {[
                { icon: "🎬", name: "Netflix", amt: "$19.99" },
                { icon: "🎵", name: "Spotify", amt: "$12.99" },
                { icon: "🏰", name: "Disney+", amt: "$11.99" },
                { icon: "🤖", name: "ChatGPT Plus", amt: "$20.00" },
                { icon: "📦", name: "Amazon Prime", amt: "$14.99" },
                { icon: "👑", name: "Max (HBO)", amt: "$16.99" },
                { icon: "🚗", name: "DoorDash", amt: "$9.99" },
                { icon: "💼", name: "LinkedIn Premium", amt: "$39.99" },
                { icon: "🍎", name: "Apple TV+", amt: "$12.99" },
                { icon: "💳", name: "Amex Platinum", amt: "$74.58" },
                { icon: "🦚", name: "Peacock", amt: "$12.99" },
                { icon: "💜", name: "Claude Pro", amt: "$20.00" },
                { icon: "🎬", name: "Netflix", amt: "$19.99" },
                { icon: "🎵", name: "Spotify", amt: "$12.99" },
                { icon: "🏰", name: "Disney+", amt: "$11.99" },
                { icon: "🤖", name: "ChatGPT Plus", amt: "$20.00" },
                { icon: "📦", name: "Amazon Prime", amt: "$14.99" },
                { icon: "👑", name: "Max (HBO)", amt: "$16.99" },
              ].map((s, i) => (
                <div key={i} className="ticker-item">
                  <span className="ticker-icon">{s.icon}</span>
                  <span className="ticker-name">{s.name}</span>
                  <span className="ticker-amt">{s.amt}/mo</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats strip */}
          <div className="stats-strip">
            <div className="stat-pill"><span className="stat-num">$1,500</span><span className="stat-label">average yearly perception gap</span></div>
            <div className="stat-divider" />
            <div className="stat-pill"><span className="stat-num">$219</span><span className="stat-label">actual monthly subscription spend</span></div>
            <div className="stat-divider" />
            <div className="stat-pill"><span className="stat-num">54%</span><span className="stat-label">have at least one unused subscription</span></div>
          </div>

          {/* Features */}
          <div className="features">
            <div className="feature-card">
              <div className="feature-icon">🔍</div>
              <div className="feature-title">Find every subscription</div>
              <div className="feature-desc">Our setup wizard covers 80+ services across streaming, AI tools, gyms, credit cards, delivery apps, and more. Tap what you pay for — done in 2 minutes.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">✂️</div>
              <div className="feature-title">Mark what to cancel</div>
              <div className="feature-desc">See your projected annual savings instantly. We track exactly when your realized savings kick in — not before your last billing date.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💚</div>
              <div className="feature-title">Watch money grow</div>
              <div className="feature-desc">A live savings tracker shows projected vs realized savings. Every dollar you stop wasting shows up here the moment your renewal date passes.</div>
            </div>
          </div>

          {/* Demo preview */}
          <div className="demo-section">
            <div className="demo-label">What it looks like inside</div>
            <div className="demo-card">
              <div className="demo-metrics">
                <div className="demo-metric">
                  <div className="demo-metric-label">Monthly spend</div>
                  <div className="demo-metric-value">$187.42</div>
                </div>
                <div className="demo-metric">
                  <div className="demo-metric-label">Yearly spend</div>
                  <div className="demo-metric-value">$2,249</div>
                </div>
                <div className="demo-metric green">
                  <div className="demo-metric-label">Saving</div>
                  <div className="demo-metric-value">$384/yr</div>
                </div>
              </div>
              <div className="demo-subs">
                {[
                  { icon: "🎬", name: "Netflix", amt: "$19.99/mo", status: "active" },
                  { icon: "🎵", name: "Spotify", amt: "$12.99/mo", status: "active" },
                  { icon: "💼", name: "LinkedIn Premium", amt: "$39.99/mo", status: "cancel" },
                  { icon: "🤖", name: "ChatGPT Plus", amt: "$20.00/mo", status: "active" },
                ].map((s, i) => (
                  <div key={i} className={`demo-sub-row ${s.status === "cancel" ? "demo-sub-cancel" : ""}`}>
                    <span className="demo-sub-icon">{s.icon}</span>
                    <span className="demo-sub-name">{s.name}</span>
                    <span className="demo-sub-amt">{s.amt}</span>
                    {s.status === "cancel" && <span className="demo-cancel-badge">✂ Cutting</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="landing-cta-bottom">
            <h2 className="landing-cta-title">Ready to take back your money?</h2>
            <p className="landing-cta-sub">Free to start. No bank connection. No credit card required.</p>
            <button className="btn-cta btn-cta-dark" onClick={handleSignIn}><GoogleIcon /> Get started with Google</button>
          </div>

          <footer className="landing-footer">
            <span className="landing-footer-brand">Subtrack</span>
            <span className="landing-footer-sep">·</span>
            <button className="landing-footer-link" onClick={() => setShowAbout(true)}>About</button>
            <span className="landing-footer-sep">·</span>
            <button className="landing-footer-link" onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
            <span className="landing-footer-sep">·</span>
            <button className="landing-footer-link" onClick={() => setShowTerms(true)}>Terms</button>
            <span className="landing-footer-sep">·</span>
            <a className="landing-footer-link" href="/cancel/" style={{ textDecoration: "none" }}>Cancel guides</a>
            <span className="landing-footer-sep">·</span>
            <a className="landing-footer-donate" href="https://donate.stripe.com/14A6oH8Xr5u333K0wi2wU00" target="_blank" rel="noopener noreferrer">Tip jar</a>
          </footer>
        </div>
      )}

      {user && (
        <div className="app-inner">
        <>
          <div className={`metrics ${cancelPending.length > 0 ? "metrics-4" : ""}`}>
            <div className="metric">
              <div className="metric-label">Monthly</div>
              <div className="metric-value">${totalMonthly.toFixed(2)}</div>
              <div className="metric-sub">per month</div>
            </div>
            <div className="metric">
              <div className="metric-label">Yearly</div>
              <div className="metric-value">${(totalMonthly * 12).toFixed(2)}</div>
              <div className="metric-sub">per year</div>
            </div>
            <div className="metric">
              <div className="metric-label">Renewing soon</div>
              <div className={`metric-value ${renewingSoon > 0 ? "danger" : ""}`}>{renewingSoon}</div>
              <div className="metric-sub">next 7 days</div>
            </div>
            {cancelPending.length > 0 && (
              <div className="metric">
                <div className="metric-label">Saving</div>
                <div className="metric-value green">${(atRiskMonthly * 12).toFixed(0)}/yr</div>
                <div className="metric-sub">projected</div>
              </div>
            )}
          </div>

          {/* Write error banner */}
          {writeError && (
            <div className="write-error-banner" role="alert">
              <span>{writeError}</span>
              <button aria-label="Dismiss error" onClick={() => setWriteError(null)}>×</button>
            </div>
          )}

          {/* Renewal reminder banner — shows 3 days before renewals */}
          {upcoming.filter(s => s.days <= 3).length > 0 && (
            <div className="renewal-banner">
              <span className="renewal-banner-icon">🔔</span>
              <div className="renewal-banner-text">
                <strong>
                  {upcoming.filter(s => s.days <= 3).map(s =>
                    s.days === 0 ? `${s.name} renews today` :
                    s.days === 1 ? `${s.name} renews tomorrow` :
                    `${s.name} renews in ${s.days} days`
                  ).join(" · ")}
                </strong>
                {" "}— want to cancel before you're charged?
              </div>
              <button className="renewal-banner-action" onClick={() => setActiveTab("subscriptions")}>
                Review
              </button>
            </div>
          )}

          <div className="tabs">
            <button className={`tab ${activeTab === "subscriptions" ? "active" : ""}`} onClick={() => setActiveTab("subscriptions")}>
              Subscriptions {activeSubs.length > 0 && <span className="tab-badge">{activeSubs.length}</span>}
            </button>
            <button className={`tab ${activeTab === "savings" ? "active" : ""}`} onClick={() => setActiveTab("savings")}>
              Savings {cancelPending.length > 0 && <span className="tab-badge savings">{cancelPending.length}</span>}
            </button>
            <button className={`tab ${activeTab === "cards" ? "active" : ""}`} onClick={() => setActiveTab("cards")}>
              Cards
            </button>
            <button className={`tab ${activeTab === "features" ? "active" : ""}`} onClick={() => setActiveTab("features")}>
              Request a Feature
            </button>
          </div>

          {activeTab === "subscriptions" && (
            <>
              <div className="section">
                <div className="section-header">
                  <div className="section-title">Your subscriptions</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn-add" onClick={() => setShowWizard(true)}>✦ Wizard</button>
                    <button className="btn-add" onClick={() => setShowForm(!showForm)}>+ Manual</button>
                  </div>
                </div>

                {showForm && (
                  <form className="add-form" onSubmit={handleAdd}>
                    <div className="form-row">
                      <div className="field"><label htmlFor="f-name">Service name</label>
                        <input id="f-name" placeholder="Netflix" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
                      <div className="field"><label htmlFor="f-amount">Amount ($)</label>
                        <input id="f-amount" type="number" placeholder="15.99" step="0.01" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></div>
                    </div>
                    <div className="form-row">
                      <div className="field"><label htmlFor="f-cycle">Billing cycle</label>
                        <select id="f-cycle" value={form.cycle} onChange={e => setForm({ ...form, cycle: e.target.value })}>
                          <option value="monthly">Monthly</option><option value="yearly">Yearly</option><option value="weekly">Weekly</option>
                        </select></div>
                      <div className="field"><label htmlFor="f-date">Next renewal</label>
                        <input id="f-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
                    </div>
                    <div className="form-row">
                      <div className="field"><label htmlFor="f-category">Category</label>
                        <select id="f-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                          {CATALOG.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                        </select></div>
                      <div className="field"><label htmlFor="f-icon">Icon (emoji)</label>
                        <input id="f-icon" placeholder="🎬" maxLength={2} value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} /></div>
                    </div>
                    <div className="form-actions">
                      <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
                      <button type="submit" className="btn-primary" disabled={addingSub}>{addingSub ? "Adding…" : "Add subscription"}</button>
                    </div>
                  </form>
                )}

                <div className="sub-list">
                  {activeSubs.length === 0 && (
                    <div className="empty">
                      <div className="empty-icon">📭</div>
                      <div>No subscriptions yet.</div>
                      <button className="btn-quick-add mt" onClick={() => setShowWizard(true)}>✦ Run setup wizard</button>
                    </div>
                  )}
                  {activeSubs.map(s => (
                    <SubCard
                      key={s.id}
                      sub={s}
                      onMarkCancel={handleMarkCancel}
                      onDelete={handleDelete}
                      onUpdateAmount={handleUpdateAmount}
                      onEdit={setEditingSub}
                    />
                  ))}
                </div>
              </div>

              {upcoming.length > 0 && (
                <div className="section">
                  <div className="section-header"><div className="section-title">Renewing soon</div></div>
                  <div className="upcoming-list">
                    {upcoming.map(s => (
                      <div key={s.id} className="upcoming-item">
                        <div>
                          <div className="upcoming-name">
                            <ServiceLogo name={s.name} fallback={s.icon || getCategoryIcon(s.category)} size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
                            {s.name}
                          </div>
                          <div className="upcoming-date">{s.days === 0 ? "Today" : s.days === 1 ? "Tomorrow" : `In ${s.days} days`}</div>
                        </div>
                        <div className="upcoming-amt">${parseFloat(s.amount).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === "savings" && <SavingsPanel
            subs={subs}
            user={user}
            meta={meta}
            onEdit={setEditingSub}
            onThankYou={(savings) => { setThankYouContext({ savings }); setShowThankYou(true); }}
            onDismissCircuitNudge={async () => {
              setMeta(m => ({ ...(m || {}), circuitFeedbackDismissed: true }));
              try { await setDoc(doc(db, "users", user.uid, "meta", "profile"), { circuitFeedbackDismissed: true }, { merge: true }); } catch (e) { console.error(e); }
            }}
          />}

          {activeTab === "cards" && <CardsPanel subs={subs} />}

          {activeTab === "settings" && (
            <EmailSettings user={user} meta={meta} onSignOut={handleSignOut} onShowTutorial={() => setShowTutorial(true)} onShowPrivacy={() => setShowPrivacy(true)} onShowTerms={() => setShowTerms(true)} onDeleteAccount={() => setShowDeleteConfirm(true)} />
          )}

          {activeTab === "features" && (
            <FeatureRequestPanel user={user} />
          )}
        </>
        </div>
      )}

      {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsOfServiceModal onClose={() => setShowTerms(false)} />}
      {showDeleteConfirm && (
        <DeleteAccountModal
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            await handleDeleteAccount();
            setShowDeleteConfirm(false);
          }}
        />
      )}

      {!cookieDismissed && (
        <CookieBanner onDismiss={() => {
          try { localStorage.setItem("st_consent", "1"); } catch {}
          setCookieDismissed(true);
        }} onPrivacy={() => setShowPrivacy(true)} />
      )}
    </div>
  );
}

// ─── MILESTONE TOAST ─────────────────────────────────────────────────────────
function MilestoneToast({ milestone, onDismiss, onSupport, onSavings }) {
  const config = {
    first_cancel: {
      icon: "✂️",
      title: `You just freed up $${Math.round(milestone.savings)}/year`,
      body: `Marking ${milestone.name} for cancellation is the first step. Once the renewal date passes, that money stays in your pocket.`,
      cta: "See your savings",
      ctaAction: onSavings,
      showSupport: true,
    },
    realized: {
      icon: "💚",
      title: `$${Math.round(milestone.savings)} officially saved`,
      body: `${milestone.name}'s last charge was your last charge. That money is yours again — every year from now on.`,
      cta: "See savings",
      ctaAction: onSavings,
      showSupport: true,
    },
    creep: {
      icon: "📈",
      title: `Your monthly spend is up $${milestone.increase?.toFixed(2)}`,
      body: `Something changed — a trial may have converted, or a price increase kicked in. Your total is now $${milestone.total?.toFixed(2)}/mo.`,
      cta: "Review subscriptions",
      ctaAction: onDismiss,
      showSupport: false,
    },
  }[milestone.type];

  if (!config) return null;

  return (
    <div className="milestone-toast">
      <button className="milestone-close" onClick={onDismiss}>×</button>
      <div className="milestone-icon">{config.icon}</div>
      <div className="milestone-title">{config.title}</div>
      <div className="milestone-body">{config.body}</div>
      <div className="milestone-actions">
        <button className="btn-milestone-cta" onClick={config.ctaAction}>{config.cta}</button>
        {config.showSupport && (
          <button className="btn-milestone-support" onClick={onSupport}>
            Let me know what helped! →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── EMAIL SETTINGS ───────────────────────────────────────────────────────────
// ─── TUTORIAL MODAL ───────────────────────────────────────────────────────────
const TUTORIAL_STEPS = [
  {
    icon: "✦",
    title: "Start by gathering your subscriptions",
    body: "Tap the ✦ Wizard button at the top to add everything you pay for — streaming, AI tools, gym memberships, credit cards. It covers 80+ services and takes about 2 minutes.",
  },
  {
    icon: "✂️",
    title: "Mark anything you want to cancel",
    body: "See a subscription you don't need? Tap the scissors icon on it to mark it for cancellation. You stay subscribed until the renewal date — we just start tracking what you'll save.",
  },
  {
    icon: "💰",
    title: "Watch your savings add up",
    body: "The Savings tab shows your projected and realized savings. Projected means what you'll save when renewals lapse. Realized means it's already in your pocket.",
  },
  {
    icon: "📈",
    title: "Put your money to work",
    body: "In Savings, the What Now section shows where to put your freed-up cash — high-yield accounts, investing platforms, and more. Cutting waste is step one; growing it is step two.",
  },
  {
    icon: "💳",
    title: "Fine-tune your cards (optional)",
    body: "If you have annual-fee credit cards, the Cards tab cross-references your subscriptions against your card benefits. See exactly what you're using, what you're leaving on the table, and whether each card is paying its way.",
  },
];

function TutorialModal({ onClose }) {
  const [step, setStep] = useState(0);
  const current = TUTORIAL_STEPS[step];
  const isLast = step === TUTORIAL_STEPS.length - 1;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet tutorial-sheet" onClick={e => e.stopPropagation()}>
        <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>

        <div className="tutorial-dots">
          {TUTORIAL_STEPS.map((_, i) => (
            <button key={i} className={`tutorial-dot ${i === step ? "active" : i < step ? "done" : ""}`} onClick={() => setStep(i)} />
          ))}
        </div>

        <div className="tutorial-step-num">Step {step + 1} of {TUTORIAL_STEPS.length}</div>
        <div className="tutorial-icon">{current.icon}</div>
        <div className="tutorial-title">{current.title}</div>
        <div className="tutorial-body">{current.body}</div>

        <div className="tutorial-actions">
          {step > 0 && (
            <button className="btn-tutorial-back" onClick={() => setStep(s => s - 1)}>← Back</button>
          )}
          {!isLast ? (
            <button className="btn-tutorial-next" onClick={() => setStep(s => s + 1)}>Next →</button>
          ) : (
            <button className="btn-tutorial-done" onClick={onClose}>Got it</button>
          )}
        </div>

        {!isLast && (
          <button className="btn-tutorial-skip" onClick={onClose}>Skip for now</button>
        )}
      </div>
    </div>
  );
}

function EmailSettings({ user, onSignOut, onShowTutorial, onShowPrivacy, onShowTerms, onDeleteAccount }) {
  const emailTypes = [
    { icon: "🔔", title: "Renewal reminders", desc: "3 days before any subscription renews — cancel before you're charged." },
    { icon: "💰", title: "Monthly savings digest", desc: "A summary of what you've saved this month, realized vs projected." },
    { icon: "📈", title: "Spend creep alerts", desc: "When your monthly total increases — a trial may have converted." },
  ];

  return (
    <div className="section">

      {/* Coming soon block */}
      <div className="settings-block settings-coming-soon">
        <div className="settings-coming-soon-badge">Coming soon</div>
        <div className="settings-block-title">Email notifications</div>
        <div className="settings-block-sub">
          Get reminded before renewals, celebrate savings milestones, and catch spend creep —
          straight to your inbox. Never marketing, always useful.
        </div>

        <div className="settings-sub-rows" style={{ opacity: 0.45, pointerEvents: "none" }}>
          {emailTypes.map(({ icon, title, desc }) => (
            <div key={title} className="settings-row">
              <div className="settings-row-icon">{icon}</div>
              <div className="settings-row-info">
                <div className="settings-row-label">{title}</div>
                <div className="settings-row-desc">{desc}</div>
              </div>
              <button className="toggle toggle-sm" disabled>
                <span className="toggle-thumb" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tip jar */}
      <div className="settings-block settings-tip-block" style={{ marginTop: "1rem" }}>
        <div className="settings-block-title">Hey Thanks Pal!</div>
        <div className="settings-tip-desc">
          Subtrack is an easy enough project that I'm hoping to always keep it free.
          If it helped you get one step closer to financial independence, I'd love to hear from ya.
          If you feel compelled to buy me a coffee, I'd bank it myself for my own journey towards financial independence!
        </div>
        <div className="settings-tip-actions">
          <a className="settings-tip-email" href="mailto:michael@scalewithme.co">michael@scalewithme.co</a>
          <div className="settings-tip-stripe">
            <stripe-buy-button
              buy-button-id="buy_btn_1TX7epFiX7wxtOZMvn5JUuv7"
              publishable-key="pk_live_51TX6uZFiX7wxtOZMKSfNlEiPWITVx5sf4Q7LtxFhZcwc2oG8T7LteFNQmarIzYt00QYt2YrujgK2imBId5dBrFbV0005SJEh56"
            />
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="settings-block" style={{ marginTop: "1rem" }}>
        <div className="settings-row settings-row-main">
          <div className="settings-row-icon">💡</div>
          <div className="settings-row-info">
            <div className="settings-row-label">How Subtrack works</div>
            <div className="settings-row-desc">Replay the 5-step walkthrough anytime</div>
          </div>
          <button className="btn-settings-action" onClick={onShowTutorial}>Show</button>
        </div>
      </div>

      {/* Account info */}
      <div className="settings-block" style={{ marginTop: "1rem" }}>
        <div className="settings-block-title">Account</div>
        <div className="settings-row">
          <div className="settings-row-icon">
            <img src={user.photoURL} alt={user.displayName || "User"} style={{ width: 28, height: 28, borderRadius: "50%" }} />
          </div>
          <div className="settings-row-info">
            <div className="settings-row-label">{user.displayName}</div>
            <div className="settings-row-desc">{user.email}</div>
          </div>
          <button className="btn-ghost" onClick={onSignOut}>Sign out</button>
        </div>
      </div>

      {/* Legal */}
      <div className="settings-block settings-legal-block" style={{ marginTop: "1rem" }}>
        <div className="settings-block-title">Legal</div>
        <div className="settings-row">
          <div className="settings-row-icon">🔒</div>
          <div className="settings-row-info">
            <div className="settings-row-label">Privacy Policy</div>
            <div className="settings-row-desc">What we collect, how we use it, your rights</div>
          </div>
          <button className="btn-settings-action" onClick={onShowPrivacy}>View</button>
        </div>
        <div className="settings-row" style={{ marginTop: 2 }}>
          <div className="settings-row-icon">📋</div>
          <div className="settings-row-info">
            <div className="settings-row-label">Terms of Service</div>
            <div className="settings-row-desc">Rules of use, disclaimers, governing law</div>
          </div>
          <button className="btn-settings-action" onClick={onShowTerms}>View</button>
        </div>
        <div className="settings-row" style={{ marginTop: 2 }}>
          <div className="settings-row-icon">🗑️</div>
          <div className="settings-row-info">
            <div className="settings-row-label">Delete account</div>
            <div className="settings-row-desc">Permanently removes all your data</div>
          </div>
          <button className="btn-settings-action btn-settings-danger" onClick={onDeleteAccount}>Delete</button>
        </div>
        <div className="settings-legal-note">
          Subtrack never accesses your bank or financial accounts. Your data is stored on Google Cloud and is never sold or shared.
        </div>
      </div>

    </div>
  );
}

// ─── DELETE ACCOUNT MODAL ────────────────────────────────────────────────────
function DeleteAccountModal({ onCancel, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleConfirm() {
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-sheet delete-sheet">
        <div className="delete-icon">🗑️</div>
        <div className="delete-title">Delete your account?</div>
        <div className="delete-body">
          This will permanently delete:
          <ul>
            <li>All your tracked subscriptions</li>
            <li>Your savings history and preferences</li>
            <li>Your Subtrack account</li>
          </ul>
          This cannot be undone. You may need to re-authenticate with Google to confirm.
        </div>
        {error && <div className="delete-error">{error}</div>}
        <div className="delete-actions">
          <button className="btn-cancel" onClick={onCancel} disabled={loading}>Keep my account</button>
          <button className="btn-delete-confirm" onClick={handleConfirm} disabled={loading}>
            {loading ? "Deleting…" : "Yes, delete everything"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PRIVACY POLICY MODAL ────────────────────────────────────────────────────
function PrivacyPolicyModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet privacy-sheet">
        <div className="privacy-header">
          <div className="privacy-title">Privacy Policy</div>
          <div className="privacy-effective">Effective June 2026</div>
          <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="privacy-body">
          <p className="privacy-intro">Subtrack is a free subscription tracking tool. This policy explains what data we collect, how we use it, and your rights. We'll keep it plain English.</p>

          <h3>What we collect</h3>
          <ul>
            <li><strong>Google account info</strong> — your name, email address, and profile photo, provided when you sign in with Google. We use this to identify your account.</li>
            <li><strong>Subscriptions you enter</strong> — service names, amounts, billing cycles, renewal dates, and cancellation status. You enter this manually; we never read your bank statements or credit card history.</li>
            <li><strong>App preferences</strong> — settings like whether you've completed the onboarding tutorial, so we don't show it again.</li>
          </ul>

          <h3>What we don't collect</h3>
          <ul>
            <li>We never access your bank accounts, credit cards, or any financial institution.</li>
            <li>We don't use advertising trackers or sell your data — ever.</li>
            <li>We don't collect your location, contacts, or anything beyond what's listed above.</li>
          </ul>

          <h3>How your data is stored</h3>
          <p>Your data is stored in Google Firebase (Firestore), hosted on Google Cloud servers in the United States. Firebase is GDPR-compliant. Data in transit is encrypted via HTTPS.</p>

          <h3>Local storage</h3>
          <p>Subtrack uses your browser's <strong>localStorage</strong> to keep you signed in between visits and to remember dismissed notices. This is strictly necessary for the app to function — no advertising or tracking data is stored locally.</p>

          <h3>Third parties</h3>
          <p>We use Google Firebase for authentication and data storage, and Stripe for optional tip-jar payments. Neither receives your subscription data. We don't share your data with any other third parties.</p>

          <h3>Your rights</h3>
          <ul>
            <li><strong>Access</strong> — all your data is visible directly in the app.</li>
            <li><strong>Deletion</strong> — open Settings and tap "Delete account." This immediately and permanently removes all your subscriptions, preferences, and Google account link from Subtrack's servers.</li>
            <li><strong>Portability</strong> — your subscription data is yours. Email us and we'll send you a full export.</li>
            <li><strong>GDPR / CCPA</strong> — if you're in the EU or California, you have the right to access, rectify, or erase your personal data. We don't sell personal information.</li>
          </ul>

          <h3>Children</h3>
          <p>Subtrack is not intended for children under 13. We do not knowingly collect data from children.</p>

          <h3>Changes</h3>
          <p>If we make material changes to this policy, we'll update the effective date above. Continued use after changes constitutes acceptance.</p>

          <h3>Contact</h3>
          <p>Questions or requests: <a href="mailto:michael@scalewithme.co">michael@scalewithme.co</a></p>
        </div>
        <div className="privacy-footer">
          <button className="btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// ─── TERMS OF SERVICE MODAL ──────────────────────────────────────────────────
function TermsOfServiceModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet privacy-sheet">
        <div className="privacy-header">
          <div className="privacy-title">Terms of Service</div>
          <div className="privacy-effective">Effective June 2026</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="privacy-body">
          <p className="privacy-intro">By using Subtrack, you agree to these terms. They're written to be readable, not to trap you — please actually read them.</p>

          <h3>What Subtrack is</h3>
          <p>Subtrack is a free, personal subscription tracking tool. It helps you organize, review, and manage the recurring services you pay for. It is not a financial advisor, bank, or payment processor.</p>

          <h3>Your account</h3>
          <p>You sign in with your Google account. You're responsible for keeping your Google credentials secure. You must be at least 13 years old to use Subtrack.</p>

          <h3>Your data</h3>
          <p>You own the data you enter. We store it on your behalf using Google Firebase. You can delete your account and all associated data at any time from Settings. See our <strong>Privacy Policy</strong> for full details on how data is handled.</p>

          <h3>Acceptable use</h3>
          <p>You agree not to:</p>
          <ul>
            <li>Use Subtrack for anything illegal or harmful</li>
            <li>Attempt to access other users' data</li>
            <li>Reverse-engineer, scrape, or abuse the service</li>
            <li>Use automated tools to interact with Subtrack in ways not intended for end users</li>
          </ul>

          <h3>The service is free — and may change</h3>
          <p>Subtrack is provided free of charge. We reserve the right to modify, pause, or discontinue the service at any time. We'll do our best to give reasonable notice for significant changes, but we can't guarantee continuity of the service.</p>

          <h3>No warranties</h3>
          <p>Subtrack is provided "as is" without warranties of any kind. We don't guarantee that the service will be error-free, always available, or that the subscription data we surface (prices, renewal dates) will always be accurate — you enter that data yourself.</p>

          <h3>Limitation of liability</h3>
          <p>To the fullest extent permitted by law, Subtrack and its creator are not liable for any indirect, incidental, or consequential damages arising from your use of the service — including missed cancellations, billing disputes, or data loss.</p>

          <h3>Referral links</h3>
          <p>The Cards tab may show referral links for credit cards. These are personal referral links. If you apply and are approved, both parties may receive a reward. We only link to products we'd recommend regardless. This doesn't affect the price you pay.</p>

          <h3>Governing law</h3>
          <p>These terms are governed by the laws of the State of California, without regard to conflict-of-law principles.</p>

          <h3>Contact</h3>
          <p>Questions: <a href="mailto:michael@scalewithme.co">michael@scalewithme.co</a></p>
        </div>
        <div className="privacy-footer">
          <button className="btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// ─── COOKIE CONSENT BANNER ───────────────────────────────────────────────────
function CookieBanner({ onDismiss, onPrivacy }) {
  return (
    <div className="cookie-banner">
      <div className="cookie-banner-text">
        Subtrack uses <strong>localStorage</strong> to keep you signed in. No tracking, no ads, no bank access.{" "}
        <button className="cookie-banner-link" onClick={onPrivacy}>Privacy Policy</button>
      </div>
      <button className="cookie-banner-btn" onClick={onDismiss}>Got it</button>
    </div>
  );
}

// ─── THANK YOU MODAL ─────────────────────────────────────────────────────────
function ThankYouModal({ context, onClose }) {
  const [shareStatus, setShareStatus] = useState(null); // "copied" | null

  function handleDonate() {
    window.open("https://donate.stripe.com/14A6oH8Xr5u333K0wi2wU00", "_blank", "noopener,noreferrer");
  }

  function handleEmail() {
    const subject = encodeURIComponent("Subtrack — thank you note");
    const body = encodeURIComponent("Hey Michael,\n\nI'd love to know what helped, and any areas you'd like more help on!\n\n");
    window.location.href = `mailto:michael@scalewithme.co?subject=${subject}&body=${body}`;
  }

  async function handleShare() {
    const url = process.env.REACT_APP_URL || "https://subtrack.fyi";
    const shareData = {
      title: "Subtrack",
      text: "I've been using Subtrack to track and cancel subscriptions I don't need — saving real money. You might like it too.",
      url,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setShareStatus("copied");
        setTimeout(() => setShareStatus(null), 2000);
      } catch (e) { console.error(e); }
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet thankyou-sheet" onClick={e => e.stopPropagation()}>
        <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>

        <div className="thankyou-header">
          <div className="thankyou-icon">💚</div>
          <div className="thankyou-title">Return the Favor!</div>
          {context?.savings > 0 && (
            <div className="thankyou-sub">${Math.round(context.savings)} back in your pocket — that's what this is about.</div>
          )}
        </div>

        <div className="thankyou-menu">
          <button className="thankyou-option" onClick={handleDonate}>
            <div className="thankyou-option-icon">💸</div>
            <div className="thankyou-option-text">
              <div className="thankyou-option-label">Donate</div>
              <div className="thankyou-option-sub">Chip in toward my own financial independence journey.</div>
            </div>
          </button>

          <button className="thankyou-option" onClick={handleEmail}>
            <div className="thankyou-option-icon">📈</div>
            <div className="thankyou-option-text">
              <div className="thankyou-option-label">Send a Thank You</div>
              <div className="thankyou-option-sub">Email me directly — I read every one.</div>
            </div>
          </button>

          <button className="thankyou-option" onClick={handleShare}>
            <div className="thankyou-option-icon">🔗</div>
            <div className="thankyou-option-text">
              <div className="thankyou-option-label">Share with a Friend</div>
              <div className="thankyou-option-sub">
                {shareStatus === "copied" ? "Link copied!" : "Pass it along to someone who's overpaying."}
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ABOUT PAGE ───────────────────────────────────────────────────────────────
function AboutPage({ onBack, onSignIn }) {
  return (
    <div className="about-page">
      <button className="about-back" onClick={onBack}>← Back</button>

      <div className="about-hero">
        <h1 className="about-h1">Built for financial independence.<br/>Starting with your subscriptions.</h1>
        <p className="about-sub">
          Most people don't realize how much they're quietly leaking every month. Once they see it, they want to do something about it.
          Subtrack is the first step — cancel what you don't need, then put that money to work.
        </p>
      </div>

      <div className="about-section">
        <h2 className="about-section-title">The mission</h2>
        <p className="about-body">
          I'm working toward financial independence and building tools to help others do the same.
          Whether you're an individual trying to get your finances in order, a small business owner with problems I haven't solved yet,
          or someone who just wants to swap notes on the journey — I'd love to connect.
        </p>
        <a className="about-link" href="mailto:michael@scalewithme.co">michael@scalewithme.co</a>
      </div>

      <div className="about-section about-tip-section">
        <h2 className="about-section-title">Hey Thanks Pal!</h2>
        <p className="about-body">
          Subtrack is an easy enough project that I'm hoping to always keep it free.
          If it helped you get one step closer to financial independence, I'd love to hear from ya.
          If you feel compelled to buy me a coffee, I'd bank it myself for my own journey towards financial independence!
        </p>
        <stripe-buy-button
          buy-button-id="buy_btn_1TX7epFiX7wxtOZMvn5JUuv7"
          publishable-key="pk_live_51TX6uZFiX7wxtOZMKSfNlEiPWITVx5sf4Q7LtxFhZcwc2oG8T7LteFNQmarIzYt00QYt2YrujgK2imBId5dBrFbV0005SJEh56"
        />
      </div>

      <div className="about-cta">
        <button className="btn-cta" onClick={onSignIn}><GoogleIcon /> Get started free</button>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
